import React, { useEffect, useState, useCallback } from "react";
import MasterLayout from "../layouts/MasterLayout";
import AppNotification from "../helpers/AppNotification";
import Swal from "sweetalert2";
import {
  RiBarChartBoxLine,
  RiRefreshLine,
  RiTelegramLine,
  RiFileCopyLine,
  RiSearch2Line,
  RiFocus3Line,
  RiCompass3Line,
  RiListOrdered,
  RiShieldFlashLine,
  RiAlertLine,
  RiTrophyLine,
} from "react-icons/ri";
import "./bank-modern.css";

const API_BASE = "/api/xsmb";

function XsmbPage() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState("tab-backtest");
  const [selectedDate, setSelectedDate] = useState("");

  // Tab Overview / Ranking state
  const [currentFilter, setCurrentFilter] = useState("ai_top15");
  const [searchNum, setSearchNum] = useState("");

  // Tab Inspect state
  const [selectedNum, setSelectedNum] = useState(0);

  // Tab Bridges state
  const [activeBridgeIdx, setActiveBridgeIdx] = useState(0);
  const [highlightPos, setHighlightPos] = useState({ posA: null, posB: null });
  const [bridgeHistory, setBridgeHistory] = useState(null);

  // Backtest & Profit Calculator state
  const [btDays, setBtDays] = useState(30);
  const [backtest, setBacktest] = useState(null);
  const [btLoading, setBtLoading] = useState(false);
  const [pts15, setPts15] = useState([10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
  const [bulkPts, setBulkPts] = useState(10);
  const [bulkCount, setBulkCount] = useState(2);
  const [costPerPt, setCostPerPt] = useState(23000);
  const [winPerPt, setWinPerPt] = useState(80000);
  const [btModelMode, setBtModelMode] = useState("ai");
  const [progBase, setProgBase] = useState(1);
  const [deBetUnit, setDeBetUnit] = useState(10000);
  const [dePayoutRatio, setDePayoutRatio] = useState(99);
  const [dailyStakingMode, setDailyStakingMode] = useState("flat");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [monthlyViewMode, setMonthlyViewMode] = useState("current");

  const copyText = (txt, label) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(txt);
    } else {
      const ta = document.createElement("textarea");
      ta.value = txt;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    AppNotification.showSuccess(`Đã sao chép ${label}: ${txt}`);
  };

  const fmtVND = (amount) => {
    const sign = amount > 0 ? "+" : amount < 0 ? "-" : "";
    return sign + Math.abs(Math.round(amount)).toLocaleString("vi-VN") + "đ";
  };

  const loadBacktestData = useCallback(async (days, dateStr) => {
    try {
      setBtLoading(true);
      setBtDays(days);
      const res = await fetch(
        `${API_BASE}/backtest?days=${days}&date=${encodeURIComponent(dateStr || "")}`
      );
      const data = await res.json();
      if (data.ok) {
        setBacktest(data.backtest);
      }
    } catch (e) {
      AppNotification.showError("Không thể tải dữ liệu kiểm chứng lịch sử");
    } finally {
      setBtLoading(false);
    }
  }, []);

  const loadBridgeHistory = useCallback(async (posA, posB, idx = 0) => {
    setActiveBridgeIdx(idx);
    setHighlightPos({ posA, posB });
    try {
      const res = await fetch(`${API_BASE}/bridge-history?posA=${posA}&posB=${posB}`);
      const data = await res.json();
      if (data.ok) {
        setBridgeHistory(data);
      }
    } catch (e) {}
  }, []);

  const loadDashboard = useCallback(
    async (dateParam = "") => {
      try {
        setLoading(true);
        const res = await fetch(
          `${API_BASE}/dashboard` + (dateParam ? `?date=${encodeURIComponent(dateParam)}` : "")
        );
        const data = await res.json();
        if (!data.ok) {
          AppNotification.showError(data.error || "Lỗi tải dữ liệu XSMB");
          return;
        }
        setState(data);
        setSelectedDate(data.lastDraw.date);
        const topList = data.rankedAI || data.rankedLoto || [];
        if (topList[0]) {
          setSelectedNum(topList[0].num);
        }
        if (data.bachThuBridges && data.bachThuBridges[0]) {
          loadBridgeHistory(
            data.bachThuBridges[0].posA,
            data.bachThuBridges[0].posB,
            0
          );
        } else {
          setBridgeHistory(null);
          setHighlightPos({ posA: null, posB: null });
        }
        await loadBacktestData(btDays, data.lastDraw.date);
      } catch (e) {
        AppNotification.showError("Lỗi kết nối máy chủ phân tích XSMB");
      } finally {
        setLoading(false);
      }
    },
    [btDays, loadBacktestData, loadBridgeHistory]
  );

  useEffect(() => {
    loadDashboard("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await fetch(`${API_BASE}/sync`, { method: "POST" });
      const data = await res.json();
      if (data.ok) {
        AppNotification.showSuccess(
          `Đã cập nhật ${data.totalDraws.toLocaleString()} kỳ quay! Mới nhất: ${data.lastDate}`
        );
        loadDashboard("");
      } else {
        AppNotification.showError(data.error || "Lỗi cập nhật KQXS");
      }
    } catch (e) {
      AppNotification.showError("Không thể đồng bộ dữ liệu mới");
    } finally {
      setSyncing(false);
    }
  };

  const handleTelegram = async () => {
    try {
      const res = await fetch(`${API_BASE}/telegram`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.ok) {
        AppNotification.showSuccess(data.message);
      } else {
        const { value: formValues } = await Swal.fire({
          title: "Cấu hình gửi báo cáo Telegram",
          html:
            '<input id="swal-tg-token" class="swal2-input" placeholder="Telegram Bot Token (@BotFather)">' +
            '<input id="swal-tg-chatid" class="swal2-input" placeholder="Telegram Chat ID nhận tin">',
          focusConfirm: false,
          showCancelButton: true,
          confirmButtonText: "Gửi báo cáo",
          cancelButtonText: "Đóng",
          preConfirm: () => {
            return {
              token: document.getElementById("swal-tg-token").value.trim(),
              chatId: document.getElementById("swal-tg-chatid").value.trim(),
            };
          },
        });
        if (formValues && formValues.token && formValues.chatId) {
          const res2 = await fetch(`${API_BASE}/telegram`, {
            method: "POST",
            body: JSON.stringify(formValues),
          });
          const data2 = await res2.json();
          if (data2.ok) AppNotification.showSuccess(data2.message);
          else AppNotification.showError(data2.error || "Gửi thất bại");
        }
      }
    } catch (e) {
      AppNotification.showError("Lỗi khi gửi báo cáo Telegram");
    }
  };

  const openNumDetail = (num) => {
    setSelectedNum(num);
    setActiveTab("tab-inspect");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Bulk & Preset Point Handlers
  const applyBulkPoints = (ptsVal, cntVal) => {
    const k = Math.min(15, Math.max(0, cntVal));
    const next = Array.from({ length: 15 }, (_, i) => (i < k ? ptsVal : 0));
    setPts15(next);
    if (ptsVal > 0) setBulkPts(ptsVal);
    if (k > 0) setBulkCount(k);
  };

  const scaleAllPoints = (factor) => {
    const next = pts15.map((cur) =>
      cur > 0 ? Math.max(1, Math.round(cur * factor)) : 0
    );
    setPts15(next);
    setBulkPts((prev) => Math.max(1, Math.round((prev || 1) * factor)));
  };

  const applyBetPreset = (arr) => {
    const next = Array.from({ length: 15 }, (_, i) => arr[i] || 0);
    setPts15(next);
  };

  const applyProgPreset = (arr, mode) => {
    const next = Array.from({ length: 15 }, (_, i) => arr[i] || 0);
    setPts15(next);
    if (mode) setDailyStakingMode(mode);
    AppNotification.showSuccess(
      "Đã áp dụng chiến thuật Gấp Thếp! Xem chi tiết từng ngày ở bảng cuối trang."
    );
  };

  // Strategy simulation helper
  const simulateStrategy = (bt, pts, cPerPt, wPerPt, useAI = true) => {
    if (!bt || !bt.dailyLogs) {
      return {
        dailyPts: 0,
        dailyCost: 0,
        totalNhayHit: 0,
        totalCost: 0,
        totalWin: 0,
        netProfit: 0,
        roi: 0,
        winDays: 0,
      };
    }
    const dailyPts = pts.reduce((a, b) => a + b, 0);
    const dailyCost = dailyPts * cPerPt;
    let totalCost = 0;
    let totalWin = 0;
    let totalNhayHit = 0;
    let winDays = 0;

    for (const log of bt.dailyLogs) {
      const nhayArr =
        useAI && log.nhayByRankAI ? log.nhayByRankAI : log.nhayByRank || [];
      let dayWinPts = 0;
      let dayNhay = 0;
      for (let i = 0; i < pts.length; i++) {
        const cnt = nhayArr[i] || 0;
        if (pts[i] > 0 && cnt > 0) {
          dayWinPts += pts[i] * cnt;
          dayNhay += cnt;
        }
      }
      const dayWin = dayWinPts * wPerPt;
      totalCost += dailyCost;
      totalWin += dayWin;
      totalNhayHit += dayNhay;
      if (dayWin >= dailyCost && dailyCost > 0) winDays++;
    }

    const netProfit = totalWin - totalCost;
    const roi = totalCost > 0 ? (netProfit / totalCost) * 100 : 0;
    return {
      dailyPts,
      dailyCost,
      totalNhayHit,
      totalCost,
      totalWin,
      netProfit,
      roi,
      winDays,
    };
  };

  const renderRhythmDots = (timeline) => {
    if (!timeline) return null;
    return (
      <div
        className="d-inline-flex align-items-center"
        style={{ gap: "3px" }}
        title="12 ngày gần nhất (Trái qua Phải, ô cuối là Hôm qua)"
      >
        {timeline.split("").map((ch, idx) => {
          const is2 = ch === "★";
          const is1 = ch === "●";
          return (
            <span
              key={idx}
              style={{
                width: "9px",
                height: "9px",
                borderRadius: "50%",
                display: "inline-block",
                background: is2
                  ? "var(--bn-vip)"
                  : is1
                  ? "var(--bn-trading-up)"
                  : "var(--wus-border-strong)",
                boxShadow: is2 ? "0 0 6px rgba(252,213,53,0.6)" : "none",
              }}
              title={is2 ? "Về ≥ 2 nháy" : is1 ? "Về 1 nháy" : "Nghỉ"}
            />
          );
        })}
      </div>
    );
  };

  const renderHitNumBadge = (numStr, defaultColor = "var(--bn-trading-up)") => {
    if (
      state &&
      state.nextActualDraw &&
      Array.isArray(state.nextActualDraw.lotoCounts)
    ) {
      const cnt = state.nextActualDraw.lotoCounts[parseInt(numStr, 10)] || 0;
      if (cnt > 0) {
        return (
          <span
            style={{
              background: "var(--wus-warning-bg)",
              color: "var(--bn-vip)",
              border: "1px solid var(--wus-warning-border)",
              padding: "1px 6px",
              borderRadius: "5px",
              fontWeight: 700,
              fontFamily: "var(--wus-mono)",
            }}
          >
            {numStr}
            {cnt > 1 ? `×${cnt}` : "✔"}
          </span>
        );
      }
      return (
        <span style={{ color: "var(--wus-text-muted)", fontFamily: "var(--wus-mono)" }}>
          {numStr}
        </span>
      );
    }
    return (
      <span
        style={{
          color: defaultColor,
          fontWeight: 700,
          fontFamily: "var(--wus-mono)",
        }}
      >
        {numStr}
      </span>
    );
  };

  if (loading && !state) {
    return (
      <MasterLayout>
        <div className="card bank-main-card">
          <div className="card-body p-5 text-center" style={{ color: "var(--wus-text-muted)" }}>
            Đang tải dữ liệu 21 năm KQXS Miền Bắc (7.604+ kỳ quay)...
          </div>
        </div>
      </MasterLayout>
    );
  }

  if (!state) return null;

  const aiTop = state.rankedAI || state.rankedLoto || [];
  const stdTop = state.rankedLoto || aiTop;
  const useAI = btModelMode === "ai";
  const activeTopList = useAI ? aiTop : stdTop;
  const bt1 = aiTop[0] || { str: "00", num: 0, aiROI: 0, probPct: 0 };
  const bt2 = aiTop[1] || { str: "01", num: 1, aiROI: 0, probPct: 0 };
  const bt3 = aiTop[2] || { str: "02", num: 2, aiROI: 0, probPct: 0 };
  const top5Str = aiTop
    .slice(0, 5)
    .map((x) => x.str)
    .join(", ");
  const top15Str = aiTop
    .slice(0, 15)
    .map((x) => x.str)
    .join(", ");
  const nextDateStr = state.meta?.nextDate || "Kỳ tới";

  // Map nextActualDraw counts
  const nextActualCounts = {};
  if (state.nextActualDraw && Array.isArray(state.nextActualDraw.lotoCounts)) {
    state.nextActualDraw.lotoCounts.forEach((cnt, idx) => {
      if (cnt > 0) nextActualCounts[String(idx).padStart(2, "0")] = cnt;
    });
  }

  // Group allLogs360 by calendar month (YYYY-MM) from start of month to end of month
  const allLogsForMonths =
    backtest && backtest.allLogs360 && backtest.allLogs360.length > 0
      ? backtest.allLogs360
      : backtest && backtest.dailyLogs
      ? backtest.dailyLogs
      : [];

  const monthlyGroups = (() => {
    const map = new Map();
    for (const log of allLogsForMonths) {
      if (!log || !log.date) continue;
      const ym = log.date.slice(0, 7);
      if (!map.has(ym)) {
        const [yr, mo] = ym.split("-");
        map.set(ym, {
          ym,
          label: `Tháng ${mo}/${yr}`,
          shortLabel: `T${parseInt(mo, 10)}/${yr}`,
          logs: [],
        });
      }
      map.get(ym).logs.push(log);
    }
    return Array.from(map.values())
      .map((g) => {
        const firstDate = g.logs[0]?.date || "";
        const lastDate = g.logs[g.logs.length - 1]?.date || "";
        const fmtD = (d) => {
          const parts = d.split("-");
          return parts.length === 3 ? `${parts[2]}/${parts[1]}` : d;
        };
        return {
          ...g,
          firstDate,
          lastDate,
          rangeStr: `${fmtD(firstDate)} → ${fmtD(lastDate)}`,
          fullRangeStr: `${fmtD(firstDate)}/${firstDate.slice(0, 4)} → ${fmtD(
            lastDate
          )}/${lastDate.slice(0, 4)}`,
          daysCount: g.logs.length,
        };
      })
      .reverse();
  })();

  const selectedMonthObj = selectedMonth
    ? monthlyGroups.find((m) => m.ym === selectedMonth) || null
    : null;

  const activeBt = selectedMonthObj
    ? {
        ...backtest,
        testDays: selectedMonthObj.logs.length,
        dailyLogs: selectedMonthObj.logs,
        periodLabel: `${selectedMonthObj.label} (${selectedMonthObj.rangeStr})`,
      }
    : backtest
    ? {
        ...backtest,
        periodLabel: `${backtest.testDays} Ngày`,
      }
    : null;

  // Current Simulation
  const sim = simulateStrategy(activeBt, pts15, costPerPt, winPerPt, useAI);
  const u = Math.max(1, Number(bulkPts) || 10);
  const presetList = [
    { name: `1. Độc Thủ Lô Top 1 (${u}đ)`, pts: [u] },
    { name: `2. Song Thủ 2 Con Đầu (Mỗi con ${u}đ)`, pts: [u, u] },
    { name: `3. Tam Thủ Top 3 Số (Mỗi con ${u}đ)`, pts: Array(3).fill(u) },
    {
      name: `4. Công-Thủ 3 Số (${u * 1.5}đ - ${u * 1.5}đ - Lót ${u * 0.5}đ)`,
      pts: [u * 1.5, u * 1.5, u * 0.5],
    },
    { name: `5. Đều Tay Top 5 Số (Mỗi con ${u}đ)`, pts: Array(5).fill(u) },
    { name: `6. Đều Tay Top 7 Số (Mỗi con ${u}đ)`, pts: Array(7).fill(u) },
    { name: `7. Đều Tay Top 10 Số (Mỗi con ${u}đ)`, pts: Array(10).fill(u) },
    { name: `8. Đều Tay Cả Top 15 Số (Mỗi con ${u}đ)`, pts: Array(15).fill(u) },
  ];

  const hasNextKnown = Boolean(state.nextActualDraw);
  const lastLog =
    backtest && backtest.dailyLogs && backtest.dailyLogs.length > 0
      ? backtest.dailyLogs[backtest.dailyLogs.length - 1]
      : null;

  const renderNextPicksCell = (ptsArr) => {
    let hitTotal = 0;
    const fmtSingle = (numStr) => {
      const cnt = nextActualCounts[numStr] || 0;
      if (cnt > 0) {
        hitTotal += cnt;
        return (
          <span
            key={numStr}
            style={{
              background: "var(--wus-warning-bg)",
              color: "var(--bn-vip)",
              border: "1px solid var(--wus-warning-border)",
              padding: "1px 6px",
              borderRadius: "4px",
              fontWeight: 700,
              fontFamily: "var(--wus-mono)",
            }}
          >
            {numStr}
            {cnt > 1 ? `×${cnt}` : "✔"}
          </span>
        );
      }
      return (
        <span
          key={numStr}
          style={{
            color: hasNextKnown ? "var(--wus-text-muted)" : "var(--bn-trading-up)",
            fontWeight: 700,
            fontFamily: "var(--wus-mono)",
          }}
        >
          {numStr}
        </span>
      );
    };

    const items = ptsArr
      .map((pt, idx) => (aiTop[idx] ? { str: aiTop[idx].str, pt } : null))
      .filter(Boolean);
    const isUnequal3 = ptsArr.length === 3 && ptsArr[0] !== ptsArr[2];

    let yHits = 0;
    const yItems =
      !hasNextKnown && lastLog && lastLog.top15PredAI && lastLog.nhayByRankAI
        ? ptsArr
            .map((_, idx) => {
              const pStr = lastLog.top15PredAI[idx];
              const c = lastLog.nhayByRankAI[idx] || 0;
              if (!pStr) return null;
              if (c > 0) yHits += c;
              return { pStr, c };
            })
            .filter(Boolean)
        : [];

    return (
      <div>
        <div className="d-flex flex-wrap align-items-center gap-1">
          {isUnequal3 ? (
            <>
              {items[0] && (
                <>
                  {fmtSingle(items[0].str)}
                  <small style={{ color: "var(--wus-text-muted)" }}>({items[0].pt}đ),</small>
                </>
              )}
              {items[1] && (
                <>
                  {fmtSingle(items[1].str)}
                  <small style={{ color: "var(--wus-text-muted)" }}>({items[1].pt}đ)</small>
                </>
              )}
              {items[2] && (
                <>
                  <span style={{ color: "var(--bn-vip)" }}>• Lót</span>
                  {fmtSingle(items[2].str)}
                  <small style={{ color: "var(--wus-text-muted)" }}>({items[2].pt}đ)</small>
                </>
              )}
            </>
          ) : (
            <>
              {items.map((it, idx) => (
                <React.Fragment key={idx}>
                  {fmtSingle(it.str)}
                  {idx < items.length - 1 && <span>, </span>}
                </React.Fragment>
              ))}
              {items.length > 0 && (
                <small style={{ color: "var(--wus-text-muted)" }}>
                  (×{items[0].pt}đ)
                </small>
              )}
            </>
          )}
          {hasNextKnown && hitTotal > 0 && (
            <span className="status-badge active ms-1">Về {hitTotal} nháy</span>
          )}
        </div>
        {yItems.length > 0 && (
          <div style={{ fontSize: "11.5px", marginTop: "4px", color: "var(--wus-text-muted)" }}>
            Kỳ {lastLog.date}:{" "}
            {yItems.map((y, idx) => (
              <React.Fragment key={idx}>
                {y.c > 0 ? (
                  <span
                    style={{
                      background: "var(--wus-warning-bg)",
                      color: "var(--bn-vip)",
                      border: "1px solid var(--wus-warning-border)",
                      padding: "0px 5px",
                      borderRadius: "4px",
                      fontWeight: 700,
                      fontFamily: "var(--wus-mono)",
                    }}
                  >
                    {y.pStr}
                    {y.c > 1 ? `×${y.c}` : "✔"}
                  </span>
                ) : (
                  <span>{y.pStr}</span>
                )}
                {idx < yItems.length - 1 ? ", " : ""}
              </React.Fragment>
            ))}
            {yHits > 0 && (
              <strong style={{ color: "var(--bn-vip)", marginLeft: "4px" }}>
                (Về {yHits} nháy)
              </strong>
            )}
          </div>
        )}
      </div>
    );
  };

  // Progressive Staking Strategies
  const progStrategies = [
    {
      name: "1. Độc Thủ Top 1 (Gấp thếp nhẹ Fibonacci: 1-2-3-5-8-13-21)",
      basePts: [1],
      seq: [1, 2, 3, 5, 8, 13, 21],
      mode: "prog_fibo",
    },
    {
      name: "2. Độc Thủ Top 1 (Gấp đôi ×2 mỗi ngày trượt: 1-2-4-8-16-32)",
      basePts: [1],
      seq: [1, 2, 4, 8, 16, 32, 64],
      mode: "prog_x2",
    },
    {
      name: "3. Song Thủ Top 2 (Chuẩn Bù Đủ Lỗ 2 Số: 1-2.5-6-15-36-85)",
      basePts: [1, 1],
      seq: [1, 2.5, 6, 15, 36, 85],
      mode: "prog_recover",
    },
    {
      name: "4. Song Thủ Top 2 (Bù Lỗ + Lãi Dày: 1-3-7-16-38-90)",
      basePts: [1, 1],
      seq: [1, 3, 7, 16, 38, 90],
      mode: "prog_recover",
    },
    {
      name: "5. Tam Thủ Top 3 Đều Tiền (Bù Đủ Lỗ 1 Nháy: 1-7.5-55-400)",
      basePts: [1, 1, 1],
      seq: [1, 7.5, 55, 400],
      mode: "prog_recover",
    },
    {
      name: "6. Công-Thủ 3 Số (1.5 - 1.5 - Lót 0.5 | Bù Đủ Lỗ: 1-3.2-10-32)",
      basePts: [1.5, 1.5, 0.5],
      seq: [1, 3.2, 10, 32, 100],
      mode: "prog_recover",
    },
    {
      name: "7. Ngũ Thủ Top 5 (Khung 3 Ngày: 1-3.5-12-42)",
      basePts: [1, 1, 1, 1, 1],
      seq: [1, 3.5, 12, 42],
      mode: "prog_recover",
    },
    {
      name: "8. Đều Tay Cả Top 15 Số (Khung Bù Đủ Lỗ: 1-3.5-12-42)",
      basePts: Array(15).fill(1),
      seq: [1, 3.5, 12, 42],
      mode: "prog_recover",
    },
  ];

  // Daily Backtest Log Calculation
  const computeDailyRows = () => {
    if (!activeBt || !activeBt.dailyLogs) return [];
    const seqMap = {
      flat: [1],
      prog_x2: [1, 2, 4, 8, 16, 32, 64, 128],
      prog_fibo: [1, 2, 3, 5, 8, 13, 21, 34, 55],
      prog_recover: [1, 2.5, 6, 15, 36, 85, 200, 460],
    };
    const activeSeq = seqMap[dailyStakingMode] || [1];
    let runningProfit = 0;
    let curStep = 0;
    let curCycleDeficit = 0;
    const mainPtActive = Math.max(1, ...pts15);
    const baseNetOneHit = Math.max(costPerPt, mainPtActive * winPerPt - sim.dailyCost);

    const rows = activeBt.dailyLogs.map((log) => {
      const nhayArr =
        useAI && log.nhayByRankAI ? log.nhayByRankAI : log.nhayByRank || [];
      const predArr =
        useAI && log.top15PredAI
          ? log.top15PredAI
          : log.top15Pred || log.top5Pred || [];
      let mult = 1;
      if (dailyStakingMode !== "flat") {
        if (curCycleDeficit === 0) {
          mult = 1;
        } else if (dailyStakingMode === "prog_recover") {
          const targetGain = baseNetOneHit * (1 + 0.15 * curStep);
          mult = Math.max(
            activeSeq[Math.min(curStep, activeSeq.length - 1)],
            Math.ceil(((curCycleDeficit + targetGain) / baseNetOneHit) * 2) / 2
          );
        } else {
          mult = activeSeq[Math.min(curStep, activeSeq.length - 1)];
        }
      }
      const stepDayNum = curStep + 1;
      const dayCost = sim.dailyCost * mult;
      let dayWinPts = 0;
      let activeNhayCount = 0;
      const hitDetails = [];

      for (let i = 0; i < pts15.length; i++) {
        const cnt = nhayArr[i] || 0;
        if (cnt > 0 && pts15[i] > 0) {
          const effPts = Math.round(pts15[i] * mult * 10) / 10;
          dayWinPts += effPts * cnt;
          activeNhayCount += cnt;
          hitDetails.push(
            `✔ #${i + 1}: ${predArr[i]} (${effPts}đ × ${cnt} nháy)`
          );
        }
      }

      const dayWin = dayWinPts * winPerPt;
      const dayNet = dayWin - dayCost;
      curCycleDeficit += dayCost - dayWin;
      const cycleNetAfterToday = -curCycleDeficit;
      runningProfit += dayNet;

      const activePreds = [];
      for (let i = 0; i < pts15.length; i++) {
        if (pts15[i] > 0 && predArr[i]) {
          const effPts = Math.round(pts15[i] * mult * 10) / 10;
          const isHit = (nhayArr[i] || 0) > 0;
          activePreds.push({
            str: predArr[i],
            effPts,
            isHit,
            rank: i + 1,
          });
        }
      }

      let cycleStatus = "flat";
      let cycleLabel = "Đều tay ×1";
      let cycleSub = "";
      if (dailyStakingMode !== "flat") {
        if (curCycleDeficit <= 0) {
          cycleStatus = "win";
          cycleLabel = `Khung N${stepDayNum} (×${mult}) → NỔ`;
          cycleSub = `Chốt khung: ${fmtVND(cycleNetAfterToday)} (Quay về N1)`;
          curStep = 0;
          curCycleDeficit = 0;
        } else if (activeNhayCount > 0) {
          cycleStatus = "partial";
          cycleLabel = `Khung N${stepDayNum} (×${mult}) → Nổ Lót`;
          cycleSub = `Còn âm khung: -${Math.round(curCycleDeficit).toLocaleString("vi-VN")}đ`;
          curStep++;
        } else {
          cycleStatus = "miss";
          cycleLabel = `Khung N${stepDayNum} (×${mult}) → Trượt`;
          cycleSub = `Vốn dồn: -${Math.round(curCycleDeficit).toLocaleString("vi-VN")}đ`;
          curStep++;
        }
      } else {
        curCycleDeficit = 0;
      }

      return {
        date: log.date,
        cycleStatus,
        cycleLabel,
        cycleSub,
        activePreds,
        activeNhayCount,
        hitDetails,
        dayCost,
        dayWin,
        dayNet,
        runningProfit,
      };
    });

    return rows.reverse();
  };

  // Filtered 100-number ranking list
  const getFilteredLotoList = () => {
    const q = searchNum.trim();
    let list =
      currentFilter === "ai_top15" || currentFilter === "all_ai"
        ? [...aiTop]
        : [...stdTop];
    if (q) {
      return list.filter((item) => item.str.includes(q));
    }
    if (currentFilter === "ai_top15" || currentFilter === "top15")
      return list.slice(0, 15);
    if (currentFilter === "sweet_spot")
      return list.filter(
        (x) =>
          x.tags.includes("Đúng nhịp") ||
          (x.currentGan >= 1 && x.currentGan <= 4 && x.freq30 >= 8)
      );
    if (currentFilter === "lo_roi")
      return list.filter((x) => x.currentGan === 0);
    if (currentFilter === "lo_kep")
      return list.filter((x) => Math.floor(x.num / 10) === x.num % 10);
    if (currentFilter === "lo_gan") return [...(state.loGanList || [])];
    return list;
  };

  const inspectStat =
    state.numberStats && state.numberStats[selectedNum]
      ? state.numberStats[selectedNum]
      : state.numberStats[0];
  const inspectRank =
    stdTop.findIndex((x) => x.num === selectedNum) + 1 || 1;

  // Traditional 27 Prizes Board Mapping
  const buildPrizeRows = () => {
    const rowDefs = [
      { label: "ĐB", indices: [0], isSpecial: true },
      { label: "G1", indices: [1], isSpecial: false },
      { label: "G2", indices: [2, 3], isSpecial: false },
      { label: "G3", indices: [4, 5, 6, 7, 8, 9], isSpecial: false },
      { label: "G4", indices: [10, 11, 12, 13], isSpecial: false },
      { label: "G5", indices: [14, 15, 16, 17, 18, 19], isSpecial: false },
      { label: "G6", indices: [20, 21, 22], isSpecial: false },
      { label: "G7", indices: [23, 24, 25, 26], isSpecial: false },
    ];
    let globalPos = 0;
    const prizeTokens = [];
    for (let p = 0; p < 27; p++) {
      const str = state.lastDraw.rawPrizes[p] || "";
      const chars = [];
      for (let c = 0; c < str.length; c++) {
        const posId = globalPos++;
        chars.push({
          posId,
          ch: str[c],
          label: state.positionLabels ? state.positionLabels[posId] : "",
        });
      }
      prizeTokens.push(chars);
    }
    return { rowDefs, prizeTokens };
  };

  const { rowDefs, prizeTokens } = buildPrizeRows();
  const byHead = Array.from({ length: 10 }, () => []);
  (state.lastDraw.lotoList || []).forEach((str) => {
    const d1 = parseInt(str[0], 10);
    if (!isNaN(d1)) byHead[d1].push(str);
  });

  return (
    <MasterLayout>
      <div className="xsmb-page-wrap">
      {/* Top Header & Action Bar */}
      <div className="bank-top-section">
        <div className="bank-action-bar">
          <div className="bank-section-heading">
            <h4 className="bank-page-title d-flex align-items-center gap-2">
              <RiBarChartBoxLine size={22} style={{ color: "var(--bn-info)", flexShrink: 0 }} />
              <span>Xác Xuất XSMB &amp; Phân Tích Chu Kỳ 21 Năm</span>
            </h4>
            <div className="bank-page-subtitle">
              Dữ liệu gốc:{" "}
              <strong style={{ color: "var(--wus-text-strong)" }}>
                {state.meta.totalHistoricalDraws.toLocaleString()} kỳ quay
              </strong>{" "}
              (2005 → {state.lastDraw.date}) &nbsp;|&nbsp; Dự báo kỳ:{" "}
              <strong style={{ color: "var(--bn-trading-up)" }}>
                {state.meta.nextDate} ({state.meta.nextDowName})
              </strong>
            </div>
          </div>

          <div className="bank-top-row-actions xsmb-top-actions">
            <div className="d-flex align-items-center gap-2 xsmb-period-select-wrap">
              <span style={{ fontSize: "12.5px", color: "var(--wus-text-muted)", whiteSpace: "nowrap" }}>
                Kỳ dữ liệu:
              </span>
              <select
                className="bank-perpage-select"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  loadDashboard(e.target.value);
                }}
              >
                {(state.recentDates || []).map((d) => (
                  <option key={d.date} value={d.date}>
                    Kỳ {d.date} (Đề {d.de})
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="bank-action-btn btn-deposit"
              onClick={handleSync}
              disabled={syncing}
            >
              <RiRefreshLine size={16} />
              <span>{syncing ? "Đang tải..." : "Cập nhật KQXS mới"}</span>
            </button>

            <button
              type="button"
              className="bank-action-btn btn-create-bank"
              onClick={handleTelegram}
            >
              <RiTelegramLine size={16} />
              <span>Báo số Telegram</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Container Card with Segmented Tabs */}
      <div className="card bank-main-card mb-4">
        <div className="bank-card-header">
          <div className="xsmb-main-tabs">
            <button
              type="button"
              className={`seg-tab-btn ${activeTab === "tab-backtest" ? "active" : ""}`}
              onClick={() => setActiveTab("tab-backtest")}
            >
              <RiTrophyLine size={15} />
              <span>1. Chốt Số &amp; Lãi/Lỗ</span>
            </button>
            <button
              type="button"
              className={`seg-tab-btn ${activeTab === "tab-inspect" ? "active" : ""}`}
              onClick={() => setActiveTab("tab-inspect")}
            >
              <RiFocus3Line size={15} />
              <span>2. Soi Số (00–99)</span>
            </button>
            <button
              type="button"
              className={`seg-tab-btn ${activeTab === "tab-bridges" ? "active" : ""}`}
              onClick={() => setActiveTab("tab-bridges")}
            >
              <RiCompass3Line size={15} />
              <span>3. KQXS &amp; Cầu Ghép</span>
            </button>
            <button
              type="button"
              className={`seg-tab-btn ${activeTab === "tab-overview" ? "active" : ""}`}
              onClick={() => setActiveTab("tab-overview")}
            >
              <RiListOrdered size={15} />
              <span>4. Xếp Hạng 100 Số</span>
            </button>
          </div>

          <span className="bank-total-pill">
            ĐB {state.lastDraw.date}: {state.lastDraw.rawPrizes[0]}
          </span>
        </div>

        <div className="card-body p-2 p-sm-3 p-md-4">
          {/* =========================================================
              TAB 1: CHỐT SỐ & TÍNH TỈ LỆ LÃI / LỖ (BACKTEST)
             ========================================================= */}
          {activeTab === "tab-backtest" && (
            <div>
              {/* 4 Summary Cards */}
              <div className="row g-3 mb-3">
                {/* Card 1 */}
                <div className="col-xl-3 col-md-6">
                  <div
                    className="wus-summary-box h-100"
                    style={{ borderColor: "var(--wus-success-border)" }}
                  >
                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                      <span className="xsmb-card-title">
                        1. ĐỘC THỦ &amp; 2. SONG THỦ ({nextDateStr})
                      </span>
                      <span className="status-badge active">Hạng #1 &amp; #2</span>
                    </div>
                    <div className="d-flex align-items-center gap-3 my-2">
                      <div
                        onClick={() => openNumDetail(bt1.num)}
                        style={{
                          width: "46px",
                          height: "46px",
                          borderRadius: "10px",
                          background: "var(--wus-success-bg)",
                          border: "1px solid var(--wus-success-border)",
                          color: "var(--bn-trading-up)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: "20px",
                          fontFamily: "var(--wus-mono)",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      >
                        {bt1.str}
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: "15px",
                            fontWeight: 700,
                            color: "var(--bn-trading-up)",
                          }}
                        >
                          Độc Thủ Top 1: {renderHitNumBadge(bt1.str, "var(--wus-text-strong)")}
                        </div>
                        <div style={{ fontSize: "12px", color: "var(--wus-text-muted)" }}>
                          ROI kỳ vọng: <strong>+{(bt1.aiROI || 0).toFixed(1)}%</strong> | XS:{" "}
                          {(bt1.probPct || 0).toFixed(1)}%
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "12.5px",
                        color: "var(--wus-text-secondary)",
                        marginTop: "8px",
                        paddingTop: "8px",
                        borderTop: "1px solid var(--wus-border)",
                      }}
                    >
                      <strong>Song Thủ 2 Con Đầu:</strong>{" "}
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => openNumDetail(bt1.num)}
                      >
                        {renderHitNumBadge(bt1.str, "var(--bn-trading-up)")}
                      </span>{" "}
                      &amp;{" "}
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => openNumDetail(bt2.num)}
                      >
                        {renderHitNumBadge(bt2.str, "var(--bn-vip)")}
                      </span>{" "}
                      <span style={{ color: "var(--wus-text-muted)" }}>(Mỗi con 10đ)</span>
                    </div>
                  </div>
                </div>

                {/* Card 2 */}
                <div className="col-xl-3 col-md-6">
                  <div
                    className="wus-summary-box h-100"
                    style={{ borderColor: "var(--wus-warning-border)" }}
                  >
                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                      <span className="xsmb-card-title">
                        3. CÔNG-THỦ &amp; 4. TAM THỦ ({nextDateStr})
                      </span>
                      <span className="bank-count-badge">Hạng #1 → #3</span>
                    </div>
                    <div style={{ fontSize: "12.5px", lineHeight: 1.55 }}>
                      <div className="fw-bold mb-1" style={{ color: "var(--wus-text-strong)" }}>
                        Công-Thủ 3 Số (15đ - 15đ - Lót 5đ):
                      </div>
                      <div
                        className="wus-info-box p-2"
                        style={{ fontSize: "12.5px" }}
                      >
                        • Chủ lực: {renderHitNumBadge(bt1.str, "var(--bn-trading-up)")}{" "}
                        <strong>(15đ)</strong> +{" "}
                        {renderHitNumBadge(bt2.str, "var(--bn-trading-up)")}{" "}
                        <strong>(15đ)</strong>
                        <br />• Đánh lót bảo hiểm:{" "}
                        {renderHitNumBadge(bt3.str, "var(--bn-vip)")} <strong>(5đ)</strong>
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "var(--wus-text-muted)",
                        marginTop: "8px",
                        paddingTop: "8px",
                        borderTop: "1px solid var(--wus-border)",
                      }}
                    >
                      <strong>Tam Thủ Đều Tay:</strong>{" "}
                      {renderHitNumBadge(bt1.str, "var(--wus-text-strong)")},{" "}
                      {renderHitNumBadge(bt2.str, "var(--wus-text-strong)")},{" "}
                      {renderHitNumBadge(bt3.str, "var(--wus-text-strong)")} (×10đ)
                    </div>
                  </div>
                </div>

                {/* Card 3 */}
                <div className="col-xl-3 col-md-6">
                  <div
                    className="wus-summary-box h-100"
                    style={{ borderColor: "var(--wus-info-border)" }}
                  >
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-1 mb-2">
                      <span className="xsmb-card-title">
                        5–8. DÀN TOP 5 • TOP 7 • TOP 10 • TOP 15
                      </span>
                      <div className="d-flex align-items-center gap-1">
                        <button
                          type="button"
                          className="xsmb-btn-sm"
                          style={{ height: "24px", padding: "0 6px", fontSize: "10.5px" }}
                          onClick={() => copyText(top5Str, "Dàn Top 5")}
                        >
                          <RiFileCopyLine size={11} />
                          <span>Top 5</span>
                        </button>
                        <button
                          type="button"
                          className="xsmb-btn-sm"
                          style={{ height: "24px", padding: "0 6px", fontSize: "10.5px" }}
                          onClick={() => copyText(top15Str, "Dàn Top 15")}
                        >
                          <RiFileCopyLine size={11} />
                          <span>Top 15</span>
                        </button>
                      </div>
                    </div>
                    <div
                      className="d-flex flex-column gap-2 mt-2"
                      style={{ fontSize: "12.5px" }}
                    >
                      <div>
                        <strong>5. Ngũ Thủ Top 5:</strong>{" "}
                        {aiTop.slice(0, 5).map((x, i) => (
                          <React.Fragment key={x.num}>
                            {renderHitNumBadge(x.str, "var(--bn-trading-up)")}
                            {i < 4 ? ", " : ""}
                          </React.Fragment>
                        ))}
                      </div>
                      <div>
                        <strong>6. Thất Thủ Top 7:</strong>{" "}
                        {aiTop.slice(0, 7).map((x, i) => (
                          <React.Fragment key={x.num}>
                            {renderHitNumBadge(x.str, "var(--wus-info-text)")}
                            {i < 6 ? ", " : ""}
                          </React.Fragment>
                        ))}
                      </div>
                      <div>
                        <strong>7. Thập Thủ Top 10:</strong>{" "}
                        {aiTop.slice(0, 10).map((x, i) => (
                          <React.Fragment key={x.num}>
                            {renderHitNumBadge(x.str, "var(--wus-text)")}
                            {i < 9 ? ", " : ""}
                          </React.Fragment>
                        ))}
                      </div>
                      <div
                        style={{
                          paddingTop: "6px",
                          borderTop: "1px dashed var(--wus-border)",
                        }}
                      >
                        <strong>8. Đều Tay Top 15 (×10đ):</strong>{" "}
                        {aiTop.slice(0, 15).map((x, i) => (
                          <React.Fragment key={x.num}>
                            {renderHitNumBadge(
                              x.str,
                              i < 5
                                ? "var(--bn-trading-up)"
                                : i < 10
                                ? "var(--wus-info-text)"
                                : "var(--bn-vip)"
                            )}
                            {i < 14 ? ", " : ""}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 4 */}
                <div className="col-xl-3 col-md-6">
                  <div className="wus-summary-box h-100">
                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                      <span className="xsmb-card-title">
                        TÓM TẮT KỲ QUAY {state.lastDraw.date}
                      </span>
                      <span className="status-badge expired">
                        ĐB: {state.lastDraw.rawPrizes[0]}
                      </span>
                    </div>
                    <div
                      className="d-flex flex-column gap-2 mt-2"
                      style={{ fontSize: "12.5px" }}
                    >
                      <div>
                        • <strong>Đề về:</strong>{" "}
                        <span
                          style={{
                            color: "var(--bn-trading-down)",
                            fontWeight: 800,
                            fontSize: "15px",
                            fontFamily: "var(--wus-mono)",
                          }}
                        >
                          {state.lastDraw.de}
                        </span>{" "}
                        (Càng: <strong>{state.lastDraw.cang}</strong> | Tổng:{" "}
                        <strong>
                          {(Math.floor(Number(state.lastDraw.de) / 10) +
                            (Number(state.lastDraw.de) % 10)) %
                            10}
                        </strong>
                        )
                      </div>
                      <div>
                        • <strong>Lô nhiều nháy:</strong>{" "}
                        {(() => {
                          const multi = [];
                          (state.lastDraw.lotoCounts || []).forEach((cnt, num) => {
                            if (cnt >= 2)
                              multi.push(`${String(num).padStart(2, "0")} (${cnt}*)`);
                          });
                          return multi.length ? (
                            <strong style={{ color: "var(--bn-vip)" }}>
                              {multi.join(", ")}
                            </strong>
                          ) : (
                            "Không có"
                          );
                        })()}
                      </div>
                      <div>
                        • <strong>Đầu/Đuôi câm:</strong>{" "}
                        {(() => {
                          const dau = [],
                            duoi = [];
                          (state.dauCountsLast || []).forEach((c, i) => {
                            if (c === 0) dau.push(i);
                          });
                          (state.duoiCountsLast || []).forEach((c, i) => {
                            if (c === 0) duoi.push(i);
                          });
                          return `${
                            dau.length ? `Đầu ${dau.join(", ")}` : "Không đầu câm"
                          } | ${
                            duoi.length ? `Đuôi ${duoi.join(", ")}` : "Không đuôi câm"
                          }`;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dàn Đề & Cảnh báo Lô Gan */}
              <div className="row g-3 mb-4">
                <div className="col-xl-6">
                  <div className="wus-summary-box h-100">
                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                      <span className="xsmb-card-title">
                        PHÂN TÍCH GIẢI ĐẶC BIỆT (CHẠM ĐỀ &amp; DÀN ĐỀ KỲ {nextDateStr}
                        {state.nextActualDraw
                          ? ` — ĐỀ VỀ: ${state.nextActualDraw.de}`
                          : ""}
                        )
                      </span>
                      <button
                        type="button"
                        className="xsmb-btn-sm"
                        style={{ height: "26px", padding: "0 10px", fontSize: "11.5px" }}
                        onClick={() =>
                          copyText(
                            state.deAnalysis.dan20.map((d) => d.str).join(", "),
                            "Dàn Đề 20 số"
                          )
                        }
                      >
                        <RiFileCopyLine size={13} />
                        <span>Copy Dàn 20 số</span>
                      </button>
                    </div>

                    <div className="d-flex flex-column gap-2" style={{ fontSize: "13px" }}>
                      <div className="d-flex flex-wrap align-items-center gap-1">
                        <span style={{ color: "var(--wus-text-muted)" }}>
                          Chạm Đề kết nhất:
                        </span>
                        {state.deAnalysis.topCham.slice(0, 3).map((c) => (
                          <span key={c.digit} className="bank-type-badge">
                            Chạm {c.digit} (nghỉ {c.gan}N)
                          </span>
                        ))}
                        <span className="mx-1" style={{ color: "var(--wus-text-subtle)" }}>
                          |
                        </span>
                        <span style={{ color: "var(--wus-text-muted)" }}>Tổng Đề:</span>
                        {state.deAnalysis.topTong.slice(0, 3).map((t) => (
                          <span key={t.tong} className="bank-count-badge">
                            Tổng {t.tong}
                          </span>
                        ))}
                      </div>
                      <div>
                        <div style={{ color: "var(--wus-text-muted)", fontSize: "12px" }}>
                          Dàn Đề 10 số rút gọn:
                        </div>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: "14px",
                            color: "var(--bn-trading-up)",
                            fontFamily: "var(--wus-mono)",
                          }}
                        >
                          {state.deAnalysis.dan10.map((d) => d.str).join(", ")}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "var(--wus-text-muted)", fontSize: "12px" }}>
                          Dàn Đề 20 số tiêu chuẩn:
                        </div>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: "13.5px",
                            color: "var(--wus-text-strong)",
                            fontFamily: "var(--wus-mono)",
                          }}
                        >
                          {state.deAnalysis.dan20.map((d) => d.str).join(", ")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="wus-summary-box h-100">
                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                      <span className="xsmb-card-title">
                        CẢNH BÁO LÔ GAN (LÂU CHƯA VỀ - HẠN CHẾ ĐÁNH)
                      </span>
                      <span className="status-badge expired">
                        <RiAlertLine size={12} /> Rủi ro cao
                      </span>
                    </div>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "var(--wus-text-muted)",
                        marginBottom: "8px",
                      }}
                    >
                      Các số đang trong chu kỳ gan dài (≥ 9 ngày chưa ra). Hệ thống đã tự động hạ điểm xác suất để bảo toàn vốn:
                    </p>
                    <div className="d-flex flex-wrap gap-2">
                      {(state.loGanList || []).slice(0, 10).map((g) => (
                        <div
                          key={g.num}
                          onClick={() => openNumDetail(g.num)}
                          className="wus-info-box py-1 px-2"
                          style={{
                            cursor: "pointer",
                            borderColor: "var(--wus-danger-border)",
                            fontSize: "12px",
                          }}
                        >
                          <strong
                            style={{
                              color: "var(--bn-trading-down)",
                              fontFamily: "var(--wus-mono)",
                              fontSize: "13.5px",
                            }}
                          >
                            {g.str}
                          </strong>
                          : Gan <strong>{g.currentGan}N</strong>{" "}
                          <span style={{ color: "var(--wus-text-muted)", fontSize: "11px" }}>
                            (Max: {g.maxGan}N)
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Backtest Controls & Bulk Point Setter */}
              <div className="wus-summary-box mb-3">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <div>
                    <h6
                      className="m-0 fw-bold"
                      style={{ color: "var(--wus-text-strong)", fontSize: "15px" }}
                    >
                      Bảng Chốt Số &amp; Tính Tỉ Lệ Lợi Nhuận Thực Tế (Ghi{" "}
                      {(costPerPt / 1000).toFixed(0)}k / Ăn {(winPerPt / 1000).toFixed(0)}k)
                    </h6>
                    <span style={{ fontSize: "12.5px", color: "var(--wus-text-muted)" }}>
                      Nhập số điểm muốn đánh cho từng con hoặc chọn nhanh chiến thuật bên dưới để tính Vốn, Tiền Trúng và Lãi/Lỗ từng ngày.
                    </span>
                  </div>

                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <div className="xsmb-pill-group">
                      {[15, 30, 60, 90, 120, 360].map((d) => (
                        <button
                          key={d}
                          type="button"
                          className={`seg-tab-btn ${
                            !selectedMonth && btDays === d ? "active" : ""
                          }`}
                          onClick={() => {
                            setSelectedMonth("");
                            loadBacktestData(d, selectedDate);
                          }}
                        >
                          {d === 360 ? "360N (1 Năm)" : `Test ${d}N`}
                        </button>
                      ))}
                    </div>

                    {monthlyGroups.length > 0 && (
                      <select
                        className="bank-perpage-select"
                        style={{
                          borderColor: selectedMonth
                            ? "var(--bn-trading-up)"
                            : undefined,
                          fontWeight: selectedMonth ? 700 : 500,
                        }}
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                      >
                        <option value="">
                          -- Lọc theo Tháng (Đầu → Cuối tháng) --
                        </option>
                        {monthlyGroups.map((m) => (
                          <option key={m.ym} value={m.ym}>
                            {m.label} ({m.rangeStr} • {m.daysCount} ngày)
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {/* Bulk Point Bar */}
                <div className="wus-info-box mb-3">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <span
                      style={{
                        fontSize: "12.5px",
                        fontWeight: 700,
                        color: "var(--bn-trading-up)",
                      }}
                    >
                      CHỈNH SỐ ĐIỂM ĐỒNG LOẠT (THAY ĐỔI CÙNG LÚC TẤT CẢ CÁC SỐ):
                    </span>
                    <div className="d-flex gap-2 flex-wrap">
                      <button
                        type="button"
                        className="xsmb-btn-sm"
                        onClick={() => scaleAllPoints(2)}
                      >
                        Nhân đôi (×2)
                      </button>
                      <button
                        type="button"
                        className="xsmb-btn-sm"
                        onClick={() => scaleAllPoints(0.5)}
                      >
                        Giảm nửa (÷2)
                      </button>
                      <button
                        type="button"
                        className="xsmb-btn-sm"
                        style={{ color: "var(--bn-trading-down)" }}
                        onClick={() => applyBulkPoints(0, 15)}
                      >
                        Xóa về 0đ
                      </button>
                    </div>
                  </div>

                  <div className="xsmb-bulk-grid">
                    <div className="xsmb-bulk-item-full">
                      <label
                        style={{
                          fontSize: "12px",
                          color: "var(--wus-text-secondary)",
                          display: "block",
                          marginBottom: "5px",
                          fontWeight: 600,
                        }}
                      >
                        1. Nhập số điểm mỗi con:
                      </label>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <input
                          type="number"
                          className="xsmb-num-input"
                          style={{ width: "80px" }}
                          value={bulkPts}
                          min={0}
                          onChange={(e) => {
                            const v = Math.max(0, parseFloat(e.target.value) || 0);
                            setBulkPts(v);
                            applyBulkPoints(v, bulkCount);
                          }}
                        />
                        <div className="xsmb-pill-group" style={{ flex: 1 }}>
                          {[1, 5, 10, 20, 50, 100].map((v) => (
                            <button
                              key={v}
                              type="button"
                              className={`seg-tab-btn ${bulkPts === v ? "active" : ""}`}
                              onClick={() => applyBulkPoints(v, bulkCount)}
                            >
                              {v}đ
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="xsmb-bulk-item-full">
                      <label
                        style={{
                          fontSize: "12px",
                          color: "var(--wus-text-secondary)",
                          display: "block",
                          marginBottom: "5px",
                          fontWeight: 600,
                        }}
                      >
                        2. Đánh đồng loạt cho mấy số đầu?
                      </label>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <input
                          type="number"
                          className="xsmb-num-input"
                          style={{ width: "70px" }}
                          value={bulkCount}
                          min={1}
                          max={15}
                          onChange={(e) => {
                            const c = Math.min(
                              15,
                              Math.max(1, parseInt(e.target.value, 10) || 1)
                            );
                            setBulkCount(c);
                            applyBulkPoints(bulkPts, c);
                          }}
                        />
                        <div className="xsmb-pill-group" style={{ flex: 1 }}>
                          {[1, 2, 3, 5, 7, 10, 15].map((c) => (
                            <button
                              key={c}
                              type="button"
                              className={`seg-tab-btn ${bulkCount === c ? "active" : ""}`}
                              onClick={() => applyBulkPoints(bulkPts, c)}
                            >
                              {c === 15 ? "Cả 15" : `Top ${c}`}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label
                        style={{
                          fontSize: "12px",
                          color: "var(--wus-text-muted)",
                          display: "block",
                          marginBottom: "5px",
                        }}
                      >
                        Giá 1 điểm (VNĐ)
                      </label>
                      <input
                        type="number"
                        className="xsmb-num-input w-100"
                        value={costPerPt}
                        step={500}
                        onChange={(e) =>
                          setCostPerPt(parseFloat(e.target.value) || 23000)
                        }
                      />
                    </div>

                    <div>
                      <label
                        style={{
                          fontSize: "12px",
                          color: "var(--wus-text-muted)",
                          display: "block",
                          marginBottom: "5px",
                        }}
                      >
                        Ăn 1 nháy (VNĐ)
                      </label>
                      <input
                        type="number"
                        className="xsmb-num-input w-100"
                        style={{
                          color: "var(--bn-trading-up)",
                        }}
                        value={winPerPt}
                        step={1000}
                        onChange={(e) =>
                          setWinPerPt(parseFloat(e.target.value) || 80000)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* 15 Individual Point Inputs */}
                <div className="wus-info-box">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "var(--wus-text-muted)",
                      }}
                    >
                      SỐ ĐIỂM CHI TIẾT TỪNG CON (HẠNG #1 → #15):
                    </span>
                    <button
                      type="button"
                      className="xsmb-btn-sm"
                      onClick={() =>
                        applyBetPreset([15, 15, 5, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0])
                      }
                    >
                      Đặt mẫu Công-Thủ (15đ - 15đ - 5đ)
                    </button>
                  </div>
                  <div className="xsmb-pts15-grid">
                    {pts15.map((val, idx) => {
                      const sNum = activeTopList[idx]?.str || "--";
                      const hitCnt = nextActualCounts[sNum] || 0;
                      return (
                        <div
                          key={idx}
                          className="xsmb-pts15-cell"
                          style={{
                            background: "var(--wus-card)",
                            padding: "6px 6px",
                            borderRadius: "8px",
                            border: `1px solid ${
                              val > 0 ? "var(--bn-trading-up)" : "var(--wus-border)"
                            }`,
                            textAlign: "center",
                          }}
                        >
                          <label
                            style={{
                              fontSize: "11.5px",
                              fontWeight: 700,
                              display: "block",
                              marginBottom: "3px",
                              fontFamily: "var(--wus-mono)",
                              color:
                                hitCnt > 0
                                  ? "var(--bn-vip)"
                                  : idx < 2
                                  ? "var(--bn-trading-up)"
                                  : "var(--wus-text-secondary)",
                            }}
                          >
                            #{idx + 1}:{sNum}
                            {hitCnt > 0 ? (hitCnt > 1 ? `×${hitCnt}` : "✔") : ""}
                          </label>
                          <input
                            type="number"
                            className="xsmb-num-input"
                            style={{
                              width: "100%",
                              height: "30px",
                              padding: "2px 4px",
                              textAlign: "center",
                            }}
                            value={val}
                            min={0}
                            onChange={(e) => {
                              const next = [...pts15];
                              next[idx] = Math.max(0, parseFloat(e.target.value) || 0);
                              setPts15(next);
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* 4 Profit Summary Cards */}
              {activeBt && (
                <div className="row g-2 g-md-3 mb-4">
                  <div className="col-xl-3 col-6">
                    <div className="wus-summary-box h-100">
                      <div className="xsmb-card-title">
                        VỐN 1 NGÀY ({sim.dailyPts}Đ)
                      </div>
                      <div className="xsmb-stat-value my-1">
                        {Math.round(sim.dailyCost).toLocaleString("vi-VN")}đ
                      </div>
                      <div style={{ fontSize: "11.5px", color: "var(--wus-text-muted)" }}>
                        Tổng vốn {activeBt.periodLabel}:{" "}
                        <strong>
                          {Math.round(sim.totalCost).toLocaleString("vi-VN")}đ
                        </strong>
                      </div>
                    </div>
                  </div>

                  <div className="col-xl-3 col-6">
                    <div className="wus-summary-box h-100">
                      <div className="xsmb-card-title">
                        NGÀY LÃI ({useAI ? "AI 21N" : "CHUẨN"})
                      </div>
                      <div
                        className="xsmb-stat-value my-1"
                        style={{ color: "var(--wus-info-text)" }}
                      >
                        {sim.winDays}/{activeBt.testDays}N (
                        {activeBt.testDays > 0
                          ? ((sim.winDays / activeBt.testDays) * 100).toFixed(0)
                          : 0}
                        %)
                      </div>
                      <div style={{ fontSize: "11.5px", color: "var(--wus-text-muted)" }}>
                        Tổng ăn: <strong>{sim.totalNhayHit} nháy</strong>
                      </div>
                    </div>
                  </div>

                  <div className="col-xl-3 col-6">
                    <div className="wus-summary-box h-100">
                      <div className="xsmb-card-title">TỔNG TIỀN TRÚNG</div>
                      <div
                        className="xsmb-stat-value my-1"
                        style={{ color: "var(--bn-vip)" }}
                      >
                        {Math.round(sim.totalWin).toLocaleString("vi-VN")}đ
                      </div>
                      <div style={{ fontSize: "11.5px", color: "var(--wus-text-muted)" }}>
                        Thưởng {Math.round(winPerPt / 1000)}k / 1 điểm
                      </div>
                    </div>
                  </div>

                  <div className="col-xl-3 col-6">
                    <div
                      className="wus-summary-box h-100"
                      style={{
                        borderColor:
                          sim.netProfit >= 0
                            ? "var(--wus-success-border)"
                            : "var(--wus-danger-border)",
                      }}
                    >
                      <div className="xsmb-card-title">
                        LÃI RÒNG ({activeBt.periodLabel.toUpperCase()})
                      </div>
                      <div
                        className="xsmb-stat-value my-1"
                        style={{
                          color:
                            sim.netProfit >= 0
                              ? "var(--bn-trading-up)"
                              : "var(--bn-trading-down)",
                        }}
                      >
                        {fmtVND(sim.netProfit)}
                      </div>
                      <div style={{ fontSize: "11.5px", color: "var(--wus-text-muted)" }}>
                        ROI:{" "}
                        <strong
                          style={{
                            color:
                              sim.netProfit >= 0
                                ? "var(--bn-trading-up)"
                                : "var(--bn-trading-down)",
                          }}
                        >
                          {sim.roi >= 0 ? "+" : ""}
                          {sim.roi.toFixed(1)}%
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Table 1: Strategy Comparison Table */}
              {activeBt && (
                <div className="card bank-main-card mb-4">
                  <div className="bank-card-header">
                    <h6 className="bank-header-title">
                      <span>
                        BẢNG ĐỐI CHIẾU CÔNG THỨC THƯỜNG vs CÔNG THỨC 21 NĂM (
                        {activeBt.periodLabel.toUpperCase()})
                      </span>
                    </h6>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span style={{ fontSize: "12px", color: "var(--wus-text-muted)" }}>
                        Chế độ tính bảng ngày:
                      </span>
                      <select
                        className="bank-perpage-select"
                        value={btModelMode}
                        onChange={(e) => setBtModelMode(e.target.value)}
                      >
                        <option value="ai">Dùng Công Thức 21 Năm Toàn Cục (Max ROI)</option>
                        <option value="std">Dùng Công Thức Thống Kê Chuẩn (2 năm)</option>
                      </select>
                    </div>
                  </div>
                  <div className="xsmb-table-wrap">
                    <table className="bank-table">
                      <thead>
                        <tr>
                          <th>Chiến Thuật Vào Điểm</th>
                          <th>
                            {hasNextKnown
                              ? `Kỳ ${nextDateStr} Đánh Con Gì (Vàng = Đã Về)`
                              : `Kỳ Tới (${nextDateStr}) Đánh Con Gì`}
                          </th>
                          <th>Vốn 1 Ngày</th>
                          <th>Tổng Vốn ({activeBt.testDays}N)</th>
                          <th>Lãi &amp; ROI (Chuẩn)</th>
                          <th>Số Nháy Ăn (21 Năm)</th>
                          <th>Lãi Ròng (21 Năm)</th>
                          <th>ROI (21 Năm)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {presetList.map((p, idx) => {
                          const resStd = simulateStrategy(
                            activeBt,
                            p.pts,
                            costPerPt,
                            winPerPt,
                            false
                          );
                          const resAI = simulateStrategy(
                            activeBt,
                            p.pts,
                            costPerPt,
                            winPerPt,
                            true
                          );
                          return (
                            <tr
                              key={idx}
                              style={{ cursor: "pointer" }}
                              onClick={() => applyBetPreset(p.pts)}
                            >
                              <td className="fw-bold" style={{ minWidth: "180px" }}>{p.name}</td>
                              <td style={{ minWidth: "160px" }}>{renderNextPicksCell(p.pts)}</td>
                              <td style={{ fontFamily: "var(--wus-mono)", whiteSpace: "nowrap" }}>
                                {Math.round(resAI.dailyCost).toLocaleString("vi-VN")}đ
                              </td>
                              <td style={{ fontFamily: "var(--wus-mono)", whiteSpace: "nowrap" }}>
                                {Math.round(resAI.totalCost).toLocaleString("vi-VN")}đ
                              </td>
                              <td
                                style={{
                                  fontFamily: "var(--wus-mono)",
                                  whiteSpace: "nowrap",
                                  color:
                                    resStd.netProfit >= 0
                                      ? "var(--wus-text)"
                                      : "var(--bn-trading-down)",
                                }}
                              >
                                {fmtVND(resStd.netProfit)}{" "}
                                <small style={{ color: "var(--wus-text-muted)" }}>
                                  ({resStd.roi >= 0 ? "+" : ""}
                                  {resStd.roi.toFixed(1)}%)
                                </small>
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                <strong>{resAI.totalNhayHit} nháy</strong> (
                                {resAI.winDays}/{activeBt.testDays} ngày lãi)
                              </td>
                              <td
                                style={{
                                  fontFamily: "var(--wus-mono)",
                                  fontWeight: 700,
                                  whiteSpace: "nowrap",
                                  color:
                                    resAI.netProfit >= 0
                                      ? "var(--bn-trading-up)"
                                      : "var(--bn-trading-down)",
                                }}
                              >
                                {fmtVND(resAI.netProfit)}
                              </td>
                              <td>
                                <span
                                  className={`status-badge ${
                                    resAI.netProfit >= 0 ? "active" : "expired"
                                  }`}
                                >
                                  {resAI.roi >= 0 ? "+" : ""}
                                  {resAI.roi.toFixed(1)}%
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Table 1B: Monthly Breakdown (Đầu Tháng -> Cuối Tháng: Tháng Ăn / Tháng Thua) */}
              {monthlyGroups.length > 0 && (
                <div className="card bank-main-card mb-4">
                  <div className="bank-card-header">
                    <div>
                      <h6 className="bank-header-title">
                        <span>
                          BẢNG THỐNG KÊ THEO THÁNG (ĐẦU THÁNG → CUỐI THÁNG) • THÁNG ĂN / THÁNG THUA
                        </span>
                      </h6>
                      <small style={{ color: "var(--wus-text-muted)", fontSize: "12px" }}>
                        Bấm vào bất kỳ tháng nào để lọc chi tiết từng ngày từ đầu tháng đến cuối tháng đó.
                      </small>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {(() => {
                        let winMonths = 0;
                        let loseMonths = 0;
                        monthlyGroups.forEach((m) => {
                          const r = simulateStrategy(
                            { testDays: m.daysCount, dailyLogs: m.logs },
                            pts15,
                            costPerPt,
                            winPerPt,
                            useAI
                          );
                          if (r.netProfit >= 0) winMonths++;
                          else loseMonths++;
                        });
                        return (
                          <span className="status-badge active">
                            Ăn {winMonths}/{monthlyGroups.length} tháng • Thua {loseMonths} tháng
                          </span>
                        );
                      })()}
                      {selectedMonth && (
                        <button
                          type="button"
                          className="xsmb-btn-sm"
                          style={{ color: "var(--bn-vip)" }}
                          onClick={() => setSelectedMonth("")}
                        >
                          Bỏ lọc tháng ({selectedMonthObj?.label})
                        </button>
                      )}
                      <div className="xsmb-pill-group">
                        <button
                          type="button"
                          className={`seg-tab-btn ${
                            monthlyViewMode === "current" ? "active" : ""
                          }`}
                          onClick={() => setMonthlyViewMode("current")}
                        >
                          Theo Chiến Thuật Đang Chọn
                        </button>
                        <button
                          type="button"
                          className={`seg-tab-btn ${
                            monthlyViewMode === "compare" ? "active" : ""
                          }`}
                          onClick={() => setMonthlyViewMode("compare")}
                        >
                          So Sánh 8 Chiến Thuật
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="xsmb-table-wrap">
                    {monthlyViewMode === "current" ? (
                      <table className="bank-table">
                        <thead>
                          <tr>
                            <th>Tháng</th>
                            <th>Từ Đầu Tháng → Cuối Tháng</th>
                            <th>Kết Quả Tháng</th>
                            <th>Ngày Ăn / Ngày Thua</th>
                            <th>Tổng Nháy Ăn</th>
                            <th>Tổng Vốn Tháng</th>
                            <th>Tổng Tiền Trúng</th>
                            <th>Lãi / Lỗ Ròng Tháng</th>
                            <th>ROI Tháng</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyGroups.map((m) => {
                            const r = simulateStrategy(
                              { testDays: m.daysCount, dailyLogs: m.logs },
                              pts15,
                              costPerPt,
                              winPerPt,
                              useAI
                            );
                            const loseDays = m.daysCount - r.winDays;
                            const isSelected = selectedMonth === m.ym;
                            const isWin = r.netProfit >= 0;
                            return (
                              <tr
                                key={m.ym}
                                style={{
                                  cursor: "pointer",
                                  background: isSelected
                                    ? "var(--wus-success-bg)"
                                    : undefined,
                                }}
                                onClick={() =>
                                  setSelectedMonth(isSelected ? "" : m.ym)
                                }
                              >
                                <td
                                  className="fw-bold"
                                  style={{
                                    whiteSpace: "nowrap",
                                    color: isSelected
                                      ? "var(--bn-trading-up)"
                                      : "var(--wus-text-strong)",
                                  }}
                                >
                                  {m.label}
                                  {isSelected && (
                                    <span className="ms-1" style={{ fontSize: "11px" }}>
                                      (Đang xem)
                                    </span>
                                  )}
                                </td>
                                <td
                                  style={{
                                    fontFamily: "var(--wus-mono)",
                                    fontSize: "12.5px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {m.fullRangeStr}{" "}
                                  <small style={{ color: "var(--wus-text-muted)" }}>
                                    ({m.daysCount} ngày)
                                  </small>
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  <span
                                    className={`status-badge ${
                                      isWin ? "active" : "expired"
                                    }`}
                                  >
                                    {isWin ? "✔ THÁNG ĂN (LÃI)" : "✘ THÁNG THUA (LỖ)"}
                                  </span>
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  <strong style={{ color: "var(--bn-trading-up)" }}>
                                    {r.winDays} ngày ăn
                                  </strong>{" "}
                                  /{" "}
                                  <span style={{ color: "var(--wus-text-muted)" }}>
                                    {loseDays} ngày thua
                                  </span>
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  <strong>{r.totalNhayHit} nháy</strong>
                                </td>
                                <td
                                  style={{
                                    fontFamily: "var(--wus-mono)",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {Math.round(r.totalCost).toLocaleString("vi-VN")}đ
                                </td>
                                <td
                                  style={{
                                    fontFamily: "var(--wus-mono)",
                                    color: "var(--bn-vip)",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {Math.round(r.totalWin).toLocaleString("vi-VN")}đ
                                </td>
                                <td
                                  style={{
                                    fontFamily: "var(--wus-mono)",
                                    fontWeight: 700,
                                    whiteSpace: "nowrap",
                                    color: isWin
                                      ? "var(--bn-trading-up)"
                                      : "var(--bn-trading-down)",
                                  }}
                                >
                                  {fmtVND(r.netProfit)}
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  <span
                                    className={`status-badge ${
                                      isWin ? "active" : "expired"
                                    }`}
                                  >
                                    {r.roi >= 0 ? "+" : ""}
                                    {r.roi.toFixed(1)}%
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <table className="bank-table">
                        <thead>
                          <tr>
                            <th>Tháng (Đầu → Cuối Tháng)</th>
                            {[
                              { short: "1. Độc Thủ", pts: presetList[0].pts },
                              { short: "2. Song Thủ", pts: presetList[1].pts },
                              { short: "3. Tam Thủ", pts: presetList[2].pts },
                              { short: "4. Công-Thủ", pts: presetList[3].pts },
                              { short: "5. Top 5 Số", pts: presetList[4].pts },
                              { short: "6. Top 7 Số", pts: presetList[5].pts },
                              { short: "7. Top 10 Số", pts: presetList[6].pts },
                              { short: "8. Top 15 Số", pts: presetList[7].pts },
                            ].map((col, cIdx) => (
                              <th
                                key={cIdx}
                                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                                onClick={() => applyBetPreset(col.pts)}
                              >
                                {col.short}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Summary row of Winning Months count */}
                          <tr style={{ background: "var(--wus-surface-soft)" }}>
                            <td className="fw-bold" style={{ whiteSpace: "nowrap" }}>
                              TỔNG SỐ THÁNG ĂN / THUA
                            </td>
                            {presetList.map((p, cIdx) => {
                              let wM = 0;
                              let totalNet = 0;
                              monthlyGroups.forEach((m) => {
                                const r = simulateStrategy(
                                  { testDays: m.daysCount, dailyLogs: m.logs },
                                  p.pts,
                                  costPerPt,
                                  winPerPt,
                                  true
                                );
                                if (r.netProfit >= 0) wM++;
                                totalNet += r.netProfit;
                              });
                              return (
                                <td key={cIdx} style={{ whiteSpace: "nowrap" }}>
                                  <div className="fw-bold" style={{ color: "var(--bn-vip)" }}>
                                    Ăn {wM}/{monthlyGroups.length} tháng
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "11.5px",
                                      fontFamily: "var(--wus-mono)",
                                      fontWeight: 700,
                                      color:
                                        totalNet >= 0
                                          ? "var(--bn-trading-up)"
                                          : "var(--bn-trading-down)",
                                    }}
                                  >
                                    Tổng: {fmtVND(totalNet)}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                          {monthlyGroups.map((m) => {
                            const isSelected = selectedMonth === m.ym;
                            return (
                              <tr
                                key={m.ym}
                                style={{
                                  cursor: "pointer",
                                  background: isSelected
                                    ? "var(--wus-success-bg)"
                                    : undefined,
                                }}
                                onClick={() =>
                                  setSelectedMonth(isSelected ? "" : m.ym)
                                }
                              >
                                <td style={{ whiteSpace: "nowrap" }}>
                                  <div className="fw-bold">{m.label}</div>
                                  <small
                                    style={{
                                      color: "var(--wus-text-muted)",
                                      fontFamily: "var(--wus-mono)",
                                    }}
                                  >
                                    {m.rangeStr} ({m.daysCount}N)
                                  </small>
                                </td>
                                {presetList.map((p, cIdx) => {
                                  const r = simulateStrategy(
                                    { testDays: m.daysCount, dailyLogs: m.logs },
                                    p.pts,
                                    costPerPt,
                                    winPerPt,
                                    true
                                  );
                                  const isWin = r.netProfit >= 0;
                                  return (
                                    <td
                                      key={cIdx}
                                      style={{
                                        fontFamily: "var(--wus-mono)",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontWeight: 700,
                                          color: isWin
                                            ? "var(--bn-trading-up)"
                                            : "var(--bn-trading-down)",
                                        }}
                                      >
                                        {fmtVND(r.netProfit)}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "var(--wus-text-muted)",
                                        }}
                                      >
                                        {isWin ? "✔ Ăn" : "✘ Thua"} • {r.totalNhayHit} nháy (
                                        {r.roi >= 0 ? "+" : ""}
                                        {r.roi.toFixed(0)}%)
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}

              {/* Table 2: Multi-Horizon 30 / 60 / 90 / 120 / 360 Days Comparison */}
              {backtest && backtest.allLogs360 && (
                <div className="card bank-main-card mb-4">
                  <div className="bank-card-header">
                    <h6 className="bank-header-title">
                      <span>
                        BẢNG ĐỐI CHIẾU LỢI NHUẬN ĐA CHU KỲ: 30 NGÀY • 60 NGÀY • 90 NGÀY • 120 NGÀY • 360 NGÀY (1 NĂM)
                      </span>
                    </h6>
                  </div>
                  <div className="xsmb-table-wrap">
                    <table className="bank-table">
                      <thead>
                        <tr>
                          <th>Chiến Thuật (21 Năm)</th>
                          <th>Kỳ Tới Đánh Con Gì</th>
                          <th>Vốn 1 Ngày</th>
                          {[30, 60, 90, 120, 360].map((h) => (
                            <th
                              key={h}
                              style={{ cursor: "pointer" }}
                              onClick={() => loadBacktestData(h, selectedDate)}
                            >
                              {h === 360 ? "360 Ngày (1 Năm)" : `${h} Ngày`}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {presetList.map((p, idx) => {
                          const dCost =
                            p.pts.reduce((a, b) => a + b, 0) * costPerPt;
                          return (
                            <tr
                              key={idx}
                              style={{ cursor: "pointer" }}
                              onClick={() => applyBetPreset(p.pts)}
                            >
                              <td className="fw-bold" style={{ minWidth: "180px" }}>{p.name}</td>
                              <td style={{ minWidth: "160px" }}>{renderNextPicksCell(p.pts)}</td>
                              <td style={{ fontFamily: "var(--wus-mono)", whiteSpace: "nowrap" }}>
                                {Math.round(dCost).toLocaleString("vi-VN")}đ
                              </td>
                              {[30, 60, 90, 120, 360].map((h) => {
                                const subBt = {
                                  testDays: h,
                                  dailyLogs: backtest.allLogs360.slice(-h),
                                };
                                const r = simulateStrategy(
                                  subBt,
                                  p.pts,
                                  costPerPt,
                                  winPerPt,
                                  true
                                );
                                return (
                                  <td key={h} style={{ fontFamily: "var(--wus-mono)", whiteSpace: "nowrap" }}>
                                    <div
                                      style={{
                                        fontWeight: 700,
                                        color:
                                          r.netProfit >= 0
                                            ? "var(--bn-trading-up)"
                                            : "var(--bn-trading-down)",
                                      }}
                                    >
                                      {fmtVND(r.netProfit)}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "var(--wus-text-muted)",
                                      }}
                                    >
                                      {r.totalNhayHit} nháy (
                                      {r.roi >= 0 ? "+" : ""}
                                      {r.roi.toFixed(1)}%)
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Table 3: Progressive Staking (Gấp Thếp) Simulator */}
              {activeBt && (
                <div className="card bank-main-card mb-4">
                  <div className="bank-card-header">
                    <h6 className="bank-header-title">
                      <span>
                        BẢNG TÍNH CHIẾN THUẬT ĐÁNH GẤP THẾP ĐẾN KHI THẮNG (HÔM NAY TRƯỢT → MAI TĂNG ĐIỂM)
                      </span>
                    </h6>
                    <div className="d-flex align-items-center gap-2">
                      <span style={{ fontSize: "12px", color: "var(--wus-text-muted)" }}>
                        Điểm khởi đầu Mốc 1:
                      </span>
                      <input
                        type="number"
                        className="xsmb-num-input"
                        style={{ width: "80px" }}
                        value={progBase}
                        min={1}
                        onChange={(e) =>
                          setProgBase(Math.max(1, parseFloat(e.target.value) || 1))
                        }
                      />
                    </div>
                  </div>
                  <div className="xsmb-table-wrap">
                    <table className="bank-table">
                      <thead>
                        <tr>
                          <th>Chiến Thuật Gấp Thếp</th>
                          <th>Kỳ Tới Đánh Con Gì (Mốc 1)</th>
                          <th>Dãy Điểm Khi Trượt Liên Tiếp</th>
                          <th>Chuỗi Trượt Dài Nhất</th>
                          <th>Vốn Dự Phòng Tối Đa</th>
                          <th>Số Chu Kỳ Thắng</th>
                          <th>Lãi Ròng Thu Về</th>
                          <th>Tỷ Suất / Vốn Dự Phòng</th>
                        </tr>
                      </thead>
                      <tbody>
                        {progStrategies.map((st, idx) => {
                          let step = 0,
                            maxLoseStreak = 0,
                            curLose = 0;
                          let totalCost = 0,
                            totalWin = 0;
                          let curCycleCost = 0,
                            curDeficit = 0,
                            maxCycleCost = 0;
                          let winCycles = 0,
                            failCycles = 0;
                          const unitPtsSum = st.basePts.reduce((a, b) => a + b, 0);
                          const baseDailyCost = unitPtsSum * progBase * costPerPt;
                          const mainPt = Math.max(...st.basePts) * progBase;
                          const mainNetPerMult = Math.max(
                            costPerPt,
                            mainPt * winPerPt - baseDailyCost
                          );

                          for (const log of activeBt.dailyLogs) {
                            const nhayArr =
                              useAI && log.nhayByRankAI
                                ? log.nhayByRankAI
                                : log.nhayByRank || [];
                            const seqMult =
                              st.seq[Math.min(step, st.seq.length - 1)];
                            const autoMult =
                              curDeficit > 0
                                ? Math.ceil(
                                    ((curDeficit + mainNetPerMult) /
                                      mainNetPerMult) *
                                      2
                                  ) / 2
                                : 1;
                            const mult =
                              (st.mode === "prog_recover"
                                ? Math.max(seqMult, autoMult)
                                : seqMult) * progBase;
                            const dayCost = unitPtsSum * mult * costPerPt;
                            let dayWin = 0;
                            for (let i = 0; i < st.basePts.length; i++) {
                              const c = nhayArr[i] || 0;
                              dayWin += st.basePts[i] * mult * c * winPerPt;
                            }
                            totalCost += dayCost;
                            totalWin += dayWin;
                            curCycleCost += dayCost;
                            curDeficit += dayCost - dayWin;
                            if (curCycleCost > maxCycleCost)
                              maxCycleCost = curCycleCost;

                            if (curDeficit <= 0) {
                              winCycles++;
                              step = 0;
                              curLose = 0;
                              curCycleCost = 0;
                              curDeficit = 0;
                            } else {
                              curLose++;
                              if (curLose > maxLoseStreak) maxLoseStreak = curLose;
                              step++;
                              if (step >= st.seq.length) {
                                failCycles++;
                                step = 0;
                                curCycleCost = 0;
                                curDeficit = 0;
                              }
                            }
                          }
                          const net = totalWin - totalCost;
                          const roiOnBankroll =
                            maxCycleCost > 0 ? (net / maxCycleCost) * 100 : 0;
                          const shownSteps = Math.min(
                            st.seq.length,
                            Math.max(3, maxLoseStreak + 1)
                          );
                          const seqStr = st.seq
                            .slice(0, shownSteps)
                            .map(
                              (x) =>
                                Math.round(x * progBase * st.basePts[0] * 10) / 10 +
                                "đ"
                            )
                            .join(" → ");
                          const presetPts = st.basePts.map((x) => x * progBase);

                          return (
                            <tr
                              key={idx}
                              style={{ cursor: "pointer" }}
                              onClick={() => applyProgPreset(presetPts, st.mode)}
                            >
                              <td className="fw-bold" style={{ minWidth: "180px" }}>{st.name}</td>
                              <td style={{ minWidth: "150px" }}>{renderNextPicksCell(presetPts)}</td>
                              <td
                                style={{
                                  fontFamily: "var(--wus-mono)",
                                  color: "var(--wus-info-text)",
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {seqStr}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                <strong>{maxLoseStreak} ngày</strong>
                              </td>
                              <td
                                style={{
                                  fontFamily: "var(--wus-mono)",
                                  fontWeight: 700,
                                  color: "var(--bn-vip)",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {Math.round(maxCycleCost).toLocaleString("vi-VN")}đ
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {failCycles === 0 ? (
                                  <span style={{ color: "var(--bn-trading-up)" }}>
                                    <strong>{winCycles} thắng</strong> (100% khung)
                                  </span>
                                ) : (
                                  <span>
                                    <strong>{winCycles} thắng</strong> /{" "}
                                    {failCycles} vượt khung
                                  </span>
                                )}
                              </td>
                              <td
                                style={{
                                  fontFamily: "var(--wus-mono)",
                                  fontWeight: 700,
                                  whiteSpace: "nowrap",
                                  color:
                                    net >= 0
                                      ? "var(--bn-trading-up)"
                                      : "var(--bn-trading-down)",
                                }}
                              >
                                {fmtVND(net)}
                              </td>
                              <td>
                                <span
                                  className={`status-badge ${
                                    net >= 0 ? "active" : "expired"
                                  }`}
                                >
                                  {roiOnBankroll >= 0 ? "+" : ""}
                                  {roiOnBankroll.toFixed(1)}% vốn
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Table 4: Đề (Special Prize) Profit Simulator */}
              {activeBt && (
                <div className="card bank-main-card mb-4">
                  <div className="bank-card-header">
                    <h6 className="bank-header-title">
                      <span>
                        BẢNG TÍNH LỢI NHUẬN ĐÁNH ĐỀ (DÀN ĐỀ 10 / 20 / 36 / 50 SỐ)
                      </span>
                    </h6>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: "12px", color: "var(--wus-text-muted)" }}>
                          Tiền ghi 1 con Đề:
                        </span>
                        <input
                          type="number"
                          className="xsmb-num-input"
                          style={{ width: "100px" }}
                          value={deBetUnit}
                          step={1000}
                          onChange={(e) =>
                            setDeBetUnit(
                              Math.max(1000, parseFloat(e.target.value) || 10000)
                            )
                          }
                        />
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: "12px", color: "var(--wus-text-muted)" }}>
                          Tỷ lệ trả thưởng:
                        </span>
                        <select
                          className="bank-perpage-select"
                          value={dePayoutRatio}
                          onChange={(e) =>
                            setDePayoutRatio(parseFloat(e.target.value) || 99)
                          }
                        >
                          <option value={99}>1 ăn 99 (Web / App)</option>
                          <option value={80}>1 ăn 80 (Ghi ngoài)</option>
                          <option value={70}>1 ăn 70 (Truyền thống)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="xsmb-table-wrap">
                    <table className="bank-table">
                      <thead>
                        <tr>
                          <th>Loại Dàn Đề</th>
                          <th>Vốn 1 Ngày</th>
                          <th>Số Ngày Nổ Đề</th>
                          <th>Tỷ Lệ Trúng Thực Tế</th>
                          <th>Tổng Vốn Bỏ Ra</th>
                          <th>Tổng Tiền Ăn Đề</th>
                          <th>Lãi Ròng Đánh Đề</th>
                          <th>Tỷ Suất Lãi (ROI)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { name: "1. Dàn Đề 10 Số Tinh Hoa", size: 10 },
                          { name: "2. Dàn Đề 20 Số Tiêu Chuẩn", size: 20 },
                          { name: "3. Dàn Đề 36 Số An Toàn", size: 36 },
                          { name: "4. Dàn Đề 50 Số (Nửa Bảng)", size: 50 },
                        ].map((d, idx) => {
                          const dailyCost = d.size * deBetUnit;
                          const totalCost = dailyCost * activeBt.testDays;
                          let hits = 0;
                          for (const log of activeBt.dailyLogs) {
                            const r = log.deRank || (log.isDe36 ? 25 : 99);
                            if (r >= 1 && r <= d.size) hits++;
                          }
                          const totalWin = hits * deBetUnit * dePayoutRatio;
                          const net = totalWin - totalCost;
                          const roi = totalCost > 0 ? (net / totalCost) * 100 : 0;
                          return (
                            <tr key={idx}>
                              <td className="fw-bold" style={{ whiteSpace: "nowrap" }}>
                                {d.name} ({d.size} số × {(deBetUnit / 1000).toFixed(0)}k)
                              </td>
                              <td style={{ fontFamily: "var(--wus-mono)", whiteSpace: "nowrap" }}>
                                {Math.round(dailyCost).toLocaleString("vi-VN")}đ
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                <strong>
                                  {hits} / {activeBt.testDays} ngày
                                </strong>
                              </td>
                              <td>
                                <span className="bank-type-badge">
                                  {activeBt.testDays > 0
                                    ? ((hits / activeBt.testDays) * 100).toFixed(1)
                                    : 0}
                                  %
                                </span>
                              </td>
                              <td style={{ fontFamily: "var(--wus-mono)", whiteSpace: "nowrap" }}>
                                {Math.round(totalCost).toLocaleString("vi-VN")}đ
                              </td>
                              <td
                                style={{
                                  fontFamily: "var(--wus-mono)",
                                  fontWeight: 700,
                                  color: "var(--bn-vip)",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {Math.round(totalWin).toLocaleString("vi-VN")}đ
                              </td>
                              <td
                                style={{
                                  fontFamily: "var(--wus-mono)",
                                  fontWeight: 700,
                                  whiteSpace: "nowrap",
                                  color:
                                    net >= 0
                                      ? "var(--bn-trading-up)"
                                      : "var(--bn-trading-down)",
                                }}
                              >
                                {fmtVND(net)}
                              </td>
                              <td>
                                <span
                                  className={`status-badge ${
                                    net >= 0 ? "active" : "expired"
                                  }`}
                                >
                                  {roi >= 0 ? "+" : ""}
                                  {roi.toFixed(1)}%
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Table 5: Daily Log Table */}
              <div className="card bank-main-card">
                <div className="bank-card-header">
                  <h6 className="bank-header-title">
                    <span>
                      BẢNG ĐỐI CHIẾU KẾT QUẢ &amp; LÃI / LỖ CHI TIẾT TỪNG NGÀY{" "}
                      {activeBt ? `(${activeBt.periodLabel.toUpperCase()})` : ""}
                    </span>
                  </h6>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span style={{ fontSize: "12px", color: "var(--wus-text-muted)" }}>
                      Chế độ tính từng ngày:
                    </span>
                    <select
                      className="bank-perpage-select"
                      value={dailyStakingMode}
                      onChange={(e) => setDailyStakingMode(e.target.value)}
                    >
                      <option value="flat">
                        Đánh Đều Tay (Cố định số điểm mỗi ngày)
                      </option>
                      <option value="prog_x2">
                        Gấp Thếp ×2 Khi Lỗ (1 → 2 → 4 → 8 → 16 → 32)
                      </option>
                      <option value="prog_fibo">
                        Gấp Thếp Nhẹ Fibonacci (1 → 2 → 3 → 5 → 8 → 13 → 21)
                      </option>
                      <option value="prog_recover">
                        Gấp Thếp Bù Đủ Lỗ + Lãi Mục Tiêu Tự Động
                      </option>
                    </select>
                  </div>
                </div>
                <div className="xsmb-table-wrap">
                  <table className="bank-table">
                    <thead>
                      <tr>
                        <th>Ngày Quay</th>
                        <th>Nhịp Khung / Hệ Số</th>
                        <th>Số Đánh &amp; Điểm Ghi</th>
                        <th>Kết Quả Nổ Thực Tế</th>
                        <th>Vốn Ghi Ngày</th>
                        <th>Tiền Ăn Ngày</th>
                        <th>Lãi / Lỗ Ngày</th>
                        <th>Tổng Lãi Lũy Kế</th>
                      </tr>
                    </thead>
                    <tbody>
                      {btLoading ? (
                        <tr>
                          <td colSpan={8} className="text-center py-4">
                            Đang tính toán kiểm chứng lịch sử...
                          </td>
                        </tr>
                      ) : (
                        computeDailyRows().map((r) => (
                          <tr key={r.date}>
                            <td
                              style={{
                                fontWeight: 700,
                                fontFamily: "var(--wus-mono)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {r.date}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              <span
                                className={`status-badge ${
                                  r.cycleStatus === "win"
                                    ? "active"
                                    : r.cycleStatus === "miss"
                                    ? "expired"
                                    : ""
                                }`}
                              >
                                {r.cycleLabel}
                              </span>
                            </td>
                            <td style={{ minWidth: "150px" }}>
                              <div className="d-flex flex-wrap gap-1">
                                {r.activePreds.map((ap, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      fontFamily: "var(--wus-mono)",
                                      fontWeight: ap.isHit ? 800 : 500,
                                      color: ap.isHit
                                        ? "var(--bn-trading-up)"
                                        : "var(--wus-text)",
                                      textDecoration: ap.isHit
                                        ? "underline"
                                        : "none",
                                    }}
                                  >
                                    {ap.str}
                                    <small style={{ color: "var(--wus-text-muted)" }}>
                                      ({ap.effPts}đ)
                                    </small>
                                    {i < r.activePreds.length - 1 ? "," : ""}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td style={{ minWidth: "160px" }}>
                              {r.hitDetails.length > 0 ? (
                                <div className="d-flex flex-wrap align-items-center gap-1">
                                  <strong>{r.activeNhayCount} nháy:</strong>
                                  {r.hitDetails.map((hd, i) => (
                                    <span key={i} className="status-badge active">
                                      {hd}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ color: "var(--wus-text-muted)" }}>
                                  Trượt (0 nháy)
                                </span>
                              )}
                            </td>
                            <td style={{ fontFamily: "var(--wus-mono)", whiteSpace: "nowrap" }}>
                              {Math.round(r.dayCost).toLocaleString("vi-VN")}đ
                            </td>
                            <td
                              style={{
                                fontFamily: "var(--wus-mono)",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                color:
                                  r.dayWin > 0
                                    ? "var(--bn-vip)"
                                    : "var(--wus-text-muted)",
                              }}
                            >
                              {Math.round(r.dayWin).toLocaleString("vi-VN")}đ
                            </td>
                            <td
                              style={{
                                fontFamily: "var(--wus-mono)",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                color:
                                  r.dayNet > 0
                                    ? "var(--bn-trading-up)"
                                    : r.dayNet < 0
                                    ? "var(--bn-trading-down)"
                                    : "var(--wus-text-muted)",
                              }}
                            >
                              <div>{fmtVND(r.dayNet)}</div>
                              {r.cycleSub && (
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "var(--wus-text-muted)",
                                    fontWeight: 500,
                                  }}
                                >
                                  {r.cycleSub}
                                </div>
                              )}
                            </td>
                            <td
                              style={{
                                fontFamily: "var(--wus-mono)",
                                fontWeight: 800,
                                whiteSpace: "nowrap",
                                color:
                                  r.runningProfit >= 0
                                    ? "var(--bn-trading-up)"
                                    : "var(--bn-trading-down)",
                              }}
                            >
                              {fmtVND(r.runningProfit)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* =========================================================
              TAB 2: SOI CHI TIẾT TỪNG CON SỐ (00 - 99)
             ========================================================= */}
          {activeTab === "tab-inspect" && (
            <div>
              <div className="wus-summary-box mb-3">
                <div className="mb-3">
                  <h6
                    className="m-0 fw-bold"
                    style={{ color: "var(--wus-text-strong)", fontSize: "15px" }}
                  >
                    Tra Cứu &amp; Giải Phẫu Xác Suất Từng Con Số (00 – 99)
                  </h6>
                  <span style={{ fontSize: "12.5px", color: "var(--wus-text-muted)" }}>
                    Chọn nhanh trên bảng 00–99 bên dưới để xem đầy đủ 5 chỉ số xác suất, chu kỳ nghỉ và bạc nhớ kéo số.
                  </span>
                </div>

                <div className="xsmb-grid-100">
                  {(state.numberStats || []).map((s) => {
                    const r =
                      stdTop.findIndex((x) => x.num === s.num) + 1;
                    const isSelected = s.num === selectedNum;
                    const isHot = r <= 10;
                    const isGan = s.currentGan >= 10;
                    return (
                      <div
                        key={s.num}
                        onClick={() => setSelectedNum(s.num)}
                        style={{
                          cursor: "pointer",
                          textAlign: "center",
                          padding: "6px 3px",
                          borderRadius: "8px",
                          background: isSelected
                            ? "var(--wus-primary)"
                            : isHot
                            ? "var(--wus-success-bg)"
                            : isGan
                            ? "var(--wus-danger-bg)"
                            : "var(--wus-card)",
                          border: `1px solid ${
                            isSelected
                              ? "var(--wus-primary)"
                              : isHot
                              ? "var(--wus-success-border)"
                              : isGan
                              ? "var(--wus-danger-border)"
                              : "var(--wus-border)"
                          }`,
                          color: isSelected
                            ? "#ffffff"
                            : "var(--wus-text-strong)",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: "13.5px",
                            fontFamily: "var(--wus-mono)",
                          }}
                        >
                          {s.str}
                        </div>
                        <div
                          style={{
                            fontSize: "10px",
                            color: isSelected
                              ? "rgba(255,255,255,0.85)"
                              : "var(--wus-text-muted)",
                          }}
                        >
                          {s.probPct.toFixed(1)}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {inspectStat && (
                <div className="wus-summary-box">
                  <div
                    className="d-flex flex-wrap justify-content-between align-items-center gap-3 pb-3 mb-3"
                    style={{ borderBottom: "1px solid var(--wus-border)" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: "54px",
                          height: "54px",
                          borderRadius: "12px",
                          background: "var(--wus-success-bg)",
                          border: "1px solid var(--wus-success-border)",
                          color: "var(--bn-trading-up)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: "24px",
                          fontFamily: "var(--wus-mono)",
                          flexShrink: 0,
                        }}
                      >
                        {inspectStat.str}
                      </div>
                      <div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <h5
                            className="m-0 fw-bold"
                            style={{ color: "var(--wus-text-strong)", fontSize: "16px" }}
                          >
                            Phân Tích Con Số {inspectStat.str}
                          </h5>
                          <span className="status-badge active">
                            Hạng #{inspectRank} / 100
                          </span>
                          {inspectStat.currentGan >= 10 ? (
                            <span className="status-badge expired">
                              Lô Gan ({inspectStat.currentGan}N chưa về)
                            </span>
                          ) : (
                            <span className="bank-count-badge">
                              Nghỉ: {inspectStat.currentGan} ngày
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            color: "var(--wus-text-secondary)",
                            fontSize: "12.5px",
                            marginTop: "4px",
                          }}
                        >
                          Xác suất kỳ tới:{" "}
                          <strong
                            style={{
                              color: "var(--bn-trading-up)",
                              fontSize: "15px",
                            }}
                          >
                            {inspectStat.probPct.toFixed(2)}%
                          </strong>{" "}
                          (Chuẩn: 23.77% | Chênh:{" "}
                          <strong>
                            {inspectStat.lift >= 1 ? "+" : ""}
                            {((inspectStat.lift - 1) * 100).toFixed(1)}%
                          </strong>
                          )
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row g-3 g-md-4">
                    <div className="col-lg-6">
                      <h6
                        className="fw-bold mb-3"
                        style={{ color: "var(--bn-vip)", fontSize: "14px" }}
                      >
                        1. Điểm Số Từ 5 Mô Hình Toán Học Thành Phần
                      </h6>
                      <div className="d-flex flex-column gap-3">
                        {[
                          {
                            label: `Bạc nhớ Markov (Các số hôm qua kéo số ${inspectStat.str})`,
                            val: inspectStat.pMarkov,
                          },
                          {
                            label: `Xác suất Chu kỳ nghỉ (Hazard Rate tại ngày nghỉ thứ ${inspectStat.currentGan})`,
                            val: inspectStat.pHazard,
                          },
                          {
                            label: "Phong độ ngắn & trung hạn (EWMA 7–90 ngày)",
                            val: inspectStat.pMomentum,
                          },
                          {
                            label: `Cầu vị trí ghép động đang báo về ${inspectStat.str}`,
                            val: inspectStat.pBridge,
                          },
                          {
                            label: `Duyên về vào ngày ${state.meta.nextDowName}`,
                            val: inspectStat.pDow,
                          },
                        ].map((m, i) => (
                          <div key={i}>
                            <div
                              className="d-flex justify-content-between gap-2 mb-1"
                              style={{ fontSize: "12.5px" }}
                            >
                              <span>• {m.label}:</span>
                              <strong style={{ fontFamily: "var(--wus-mono)", whiteSpace: "nowrap" }}>
                                {(m.val * 100).toFixed(1)}%
                              </strong>
                            </div>
                            <div
                              style={{
                                width: "100%",
                                height: "7px",
                                borderRadius: "99px",
                                background: "var(--wus-card)",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  width: `${Math.min(100, m.val * 200)}%`,
                                  height: "100%",
                                  background: "var(--bn-trading-up)",
                                  borderRadius: "99px",
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <h6
                        className="fw-bold mb-3"
                        style={{
                          color: "var(--bn-trading-up)",
                          fontSize: "14px",
                        }}
                      >
                        2. Lịch Sử Chu Kỳ, Nhịp Về &amp; Bằng Chứng Bạc Nhớ
                      </h6>
                      <div
                        className="wus-info-box d-flex flex-column gap-2"
                        style={{ fontSize: "12.5px" }}
                      >
                        <div>
                          • <strong>Số ngày chưa về hiện tại:</strong>{" "}
                          <strong style={{ color: "var(--bn-vip)" }}>
                            {inspectStat.currentGan} ngày
                          </strong>{" "}
                          (TB cứ{" "}
                          <strong>{inspectStat.meanCycle.toFixed(1)} ngày</strong>{" "}
                          về 1 lần; Gan kỷ lục: {inspectStat.maxGan} ngày)
                        </div>
                        <div>
                          • <strong>Tần suất xuất hiện:</strong> 7 ngày qua về{" "}
                          <strong>{inspectStat.freq7} lần</strong> | 30 ngày qua về{" "}
                          <strong>
                            {inspectStat.freq30} ngày ({inspectStat.nhay30} nháy)
                          </strong>{" "}
                          | 90 ngày qua về{" "}
                          <strong>{inspectStat.freq90} lần</strong>
                        </div>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                          <span>• <strong>Nhịp 12 ngày gần nhất:</strong></span>
                          {renderRhythmDots(inspectStat.timeline)}
                        </div>
                        {inspectStat.bestBridge && (
                          <div>
                            • <strong>Cầu vị trí đang ăn thông:</strong> Ghép{" "}
                            <span className="bank-type-badge">
                              {inspectStat.bestBridge.label}
                            </span>{" "}
                            đã ăn thông{" "}
                            <strong>
                              {inspectStat.bestBridge.streak} ngày liên tiếp
                            </strong>
                          </div>
                        )}
                        <div
                          className="pt-2 mt-1"
                          style={{ borderTop: "1px solid var(--wus-border)" }}
                        >
                          <strong>
                            Bằng chứng Bạc Nhớ từ KQXS ngày {state.lastDraw.date}:
                          </strong>
                          <ul
                            className="ps-3 mt-1 mb-0"
                            style={{
                              color: "var(--wus-text-secondary)",
                              fontSize: "12px",
                            }}
                          >
                            {(inspectStat.bacNho || []).map((ev, i) => (
                              <li key={i}>
                                {ev.desc}{" "}
                                <strong>(Mức độ kéo: {ev.lift.toFixed(2)}x)</strong>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* =========================================================
              TAB 3: BẢNG KẾT QUẢ & SOI CẦU GHÉP VỊ TRÍ
             ========================================================= */}
          {activeTab === "tab-bridges" && (
            <div className="row g-3 g-md-4">
              {/* Left: 27 Prizes Board */}
              <div className="col-lg-6">
                <div className="wus-summary-box mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <span className="xsmb-card-title">
                      BẢNG KẾT QUẢ XSMB NGÀY {state.lastDraw.date}
                    </span>
                    <span style={{ fontSize: "11px", color: "var(--bn-vip)" }}>
                      Bấm vào Cầu để tô sáng vị trí ghép số
                    </span>
                  </div>

                  <div className="xsmb-table-wrap">
                    <table className="bank-table">
                      <tbody>
                        {rowDefs.map((r) => (
                          <tr key={r.label}>
                            <td
                              style={{
                                width: "48px",
                                fontWeight: 700,
                                textAlign: "center",
                                background: "var(--wus-table-head-bg)",
                                color: "var(--wus-text-muted)",
                              }}
                            >
                              {r.label}
                            </td>
                            <td className="text-center">
                              <div className="d-flex flex-wrap justify-content-center gap-1 gap-sm-2">
                                {r.indices.map((idx) => (
                                  <span
                                    key={idx}
                                    style={{
                                      fontFamily: "var(--wus-mono)",
                                      fontSize: r.isSpecial ? "18px" : "14px",
                                      fontWeight: 700,
                                      padding: "2px 6px",
                                      borderRadius: "6px",
                                      background: r.isSpecial
                                        ? "var(--wus-danger-bg)"
                                        : "var(--wus-card-inner)",
                                      color: r.isSpecial
                                        ? "var(--bn-trading-down)"
                                        : "var(--wus-text-strong)",
                                      border: r.isSpecial
                                        ? "1px solid var(--wus-danger-border)"
                                        : "1px solid var(--wus-border)",
                                    }}
                                  >
                                    {(prizeTokens[idx] || []).map((tk) => {
                                      const isA = tk.posId === highlightPos.posA;
                                      const isB = tk.posId === highlightPos.posB;
                                      return (
                                        <span
                                          key={tk.posId}
                                          title={`Vị trí: ${tk.label}`}
                                          style={{
                                            display: "inline-block",
                                            padding: "0 1.5px",
                                            borderRadius: "4px",
                                            background: isA
                                              ? "var(--bn-trading-down)"
                                              : isB
                                              ? "var(--bn-vip)"
                                              : "transparent",
                                            color: isA
                                              ? "#fff"
                                              : isB
                                              ? "#181a20"
                                              : "inherit",
                                          }}
                                        >
                                          {tk.ch}
                                        </span>
                                      );
                                    })}
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Bảng Đầu Đuôi */}
                <div className="wus-summary-box">
                  <div className="xsmb-card-title mb-2">
                    BẢNG LÔ TÔ ĐẦU - ĐUÔI KỲ {state.lastDraw.date}
                  </div>
                  <div className="xsmb-table-wrap">
                    <table className="bank-table">
                      <tbody>
                        {byHead.map((arr, head) => (
                          <tr key={head}>
                            <td
                              style={{
                                width: "65px",
                                fontWeight: 700,
                                textAlign: "center",
                                background: "var(--wus-table-head-bg)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Đầu {head}
                            </td>
                            <td
                              style={{
                                fontFamily: "var(--wus-mono)",
                                fontWeight: 600,
                              }}
                            >
                              {arr.length ? (
                                arr.join(", ")
                              ) : (
                                <span style={{ color: "var(--bn-trading-down)" }}>
                                  Câm
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Right: Interactive Bridges */}
              <div className="col-lg-6">
                <div className="wus-summary-box">
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                    <span className="xsmb-card-title">
                      CẦU GHÉP VỊ TRÍ ĐANG ĂN THÔNG LIÊN TIẾP
                    </span>
                    <span className="status-badge active">
                      5.671 vị trí
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: "12px",
                      color: "var(--wus-text-muted)",
                      marginBottom: "10px",
                    }}
                  >
                    Bấm vào 1 cầu bên dưới để xem 2 vị trí ghép số sáng lên trên bảng KQXS và lịch sử ăn thông:
                  </p>

                  <div className="d-flex flex-column gap-2 mb-3">
                    {(state.bachThuBridges || []).slice(0, 10).map((b, idx) => {
                      const predStr = String(b.predicted).padStart(2, "0");
                      const revStr = predStr[1] + predStr[0];
                      const isActive = idx === activeBridgeIdx;
                      return (
                        <div
                          key={idx}
                          onClick={() => loadBridgeHistory(b.posA, b.posB, idx)}
                          className="wus-info-box d-flex justify-content-between align-items-center xsmb-bridge-card"
                          style={{
                            cursor: "pointer",
                            borderColor: isActive
                              ? "var(--bn-vip)"
                              : "var(--wus-border-strong)",
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: "13px",
                                color: "var(--wus-text-strong)",
                              }}
                            >
                              Cầu thông{" "}
                              <span style={{ color: "var(--bn-trading-up)" }}>
                                {b.streak}N
                              </span>
                              :{" "}
                              <span style={{ color: "var(--bn-vip)" }}>
                                {b.label}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: "11.5px",
                                color: "var(--wus-text-muted)",
                              }}
                            >
                              Ghép{" "}
                              <strong style={{ color: "var(--bn-trading-down)" }}>
                                {b.label.split(" + ")[0]}
                              </strong>{" "}
                              với{" "}
                              <strong style={{ color: "var(--bn-vip)" }}>
                                {b.label.split(" + ")[1]}
                              </strong>
                            </div>
                          </div>
                          <div className="text-end" style={{ flexShrink: 0 }}>
                            <div
                              style={{
                                fontSize: "10.5px",
                                color: "var(--wus-text-muted)",
                              }}
                            >
                              Báo kỳ tới
                            </div>
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: 800,
                                color: "var(--bn-trading-up)",
                                fontFamily: "var(--wus-mono)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {predStr}{" "}
                              <small
                                style={{
                                  fontSize: "11.5px",
                                  color: "var(--wus-text-muted)",
                                }}
                              >
                                (Lót {revStr})
                              </small>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {bridgeHistory && (
                    <div className="wus-info-box">
                      <div
                        className="fw-bold mb-2"
                        style={{ fontSize: "12.5px", color: "var(--bn-vip)" }}
                      >
                        Lịch sử ăn thông của cầu [{bridgeHistory.labelA} +{" "}
                        {bridgeHistory.labelB}]:
                      </div>
                      <div className="xsmb-table-wrap">
                        <table className="bank-table">
                          <thead>
                            <tr>
                              <th>Lấy từ KQ ngày</th>
                              <th>Ghép 2 vị trí</th>
                              <th>Đánh cho ngày</th>
                              <th>Kết quả</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(bridgeHistory.history || []).map((h, i) => (
                              <tr key={i}>
                                <td style={{ fontFamily: "var(--wus-mono)", whiteSpace: "nowrap" }}>
                                  {h.srcDate}
                                </td>
                                <td style={{ fontFamily: "var(--wus-mono)", whiteSpace: "nowrap" }}>
                                  <strong style={{ color: "var(--bn-trading-down)" }}>
                                    {h.digitA}
                                  </strong>{" "}
                                  ghép{" "}
                                  <strong style={{ color: "var(--bn-vip)" }}>
                                    {h.digitB}
                                  </strong>{" "}
                                  → <strong>{h.predicted}</strong>
                                </td>
                                <td style={{ fontFamily: "var(--wus-mono)", whiteSpace: "nowrap" }}>
                                  {h.dstDate}
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  {h.isNext ? (
                                    <span className="bank-type-badge">
                                      Dự báo: {h.predicted}
                                    </span>
                                  ) : h.hitBT > 0 ? (
                                    <span className="status-badge active">
                                      Ăn thẳng {h.predicted} ({h.hitBT}*)
                                    </span>
                                  ) : h.hitRev > 0 ? (
                                    <span className="bank-count-badge">
                                      Ăn lộn {h.reverse}
                                    </span>
                                  ) : (
                                    <span style={{ color: "var(--wus-text-muted)" }}>
                                      Trượt
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* =========================================================
              TAB 4: BẢNG XẾP HẠNG 100 CON SỐ (00 - 99)
             ========================================================= */}
          {activeTab === "tab-overview" && (
            <div>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="xsmb-pill-group">
                  {[
                    { id: "ai_top15", label: "Top 15 (21 Năm)" },
                    { id: "top15", label: "Top 15 Chuẩn" },
                    { id: "all_ai", label: "Cả 100 Số" },
                    { id: "sweet_spot", label: "Đúng Nhịp" },
                    { id: "lo_roi", label: "Lô Rơi" },
                    { id: "lo_kep", label: "Lô Kép" },
                    { id: "lo_gan", label: "Lô Gan" },
                  ].map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      className={`seg-tab-btn ${
                        currentFilter === f.id ? "active" : ""
                      }`}
                      onClick={() => setCurrentFilter(f.id)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="bank-search-input-box" style={{ minWidth: "200px" }}>
                  <RiSearch2Line size={15} className="search-box-icon" />
                  <input
                    type="text"
                    className="bank-search-input"
                    placeholder="Tìm số (VD: 52, 68)..."
                    value={searchNum}
                    onChange={(e) => setSearchNum(e.target.value)}
                  />
                </div>
              </div>

              <div className="xsmb-table-wrap">
                <table className="bank-table">
                  <thead>
                    <tr>
                      <th>Hạng</th>
                      <th>Con Số</th>
                      <th>Công Thức 21 Năm (Max ROI)</th>
                      <th>Gợi Ý Vào Tiền Kỳ Tới</th>
                      <th>Xác Suất Thống Kê</th>
                      <th>Trạng Thái Chu Kỳ (Gan)</th>
                      <th>Phong Độ 7 / 30 Ngày</th>
                      <th>Nhịp 12 Ngày</th>
                      <th>Lý Do &amp; Tín Hiệu Báo Về</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredLotoList().map((item) => {
                      const stdRank =
                        stdTop.findIndex((x) => x.num === item.num) + 1;
                      const aiRank = item.aiRank || stdRank;
                      const aiRoiVal =
                        item.aiROI !== undefined
                          ? item.aiROI
                          : ((item.prob * 80 - 23) / 23) * 100;
                      const aiExpNhay =
                        item.aiExpectedNhay !== undefined
                          ? item.aiExpectedNhay
                          : item.prob * 1.15;
                      const topReason =
                        item.bacNho && item.bacNho[0] ? item.bacNho[0].desc : "";

                      return (
                        <tr
                          key={item.num}
                          style={{ cursor: "pointer" }}
                          onClick={() => openNumDetail(item.num)}
                        >
                          <td style={{ fontFamily: "var(--wus-mono)", whiteSpace: "nowrap" }}>
                            <div
                              style={{
                                fontWeight: 700,
                                color: "var(--bn-trading-up)",
                              }}
                            >
                              #{aiRank}
                            </div>
                            <small style={{ color: "var(--wus-text-muted)" }}>
                              Chuẩn: #{stdRank}
                            </small>
                          </td>
                          <td>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "36px",
                                height: "36px",
                                borderRadius: "8px",
                                fontWeight: 800,
                                fontFamily: "var(--wus-mono)",
                                fontSize: "15px",
                                background:
                                  aiRank <= 5
                                    ? "var(--wus-success-bg)"
                                    : aiRank <= 15
                                    ? "var(--wus-warning-bg)"
                                    : "var(--wus-card-inner)",
                                color:
                                  aiRank <= 5
                                    ? "var(--bn-trading-up)"
                                    : aiRank <= 15
                                    ? "var(--bn-vip)"
                                    : "var(--wus-text-strong)",
                                border: "1px solid var(--wus-border-strong)",
                              }}
                            >
                              {item.str}
                            </span>
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <div className="d-flex align-items-center gap-2">
                              <strong
                                style={{
                                  color: "var(--bn-trading-up)",
                                  fontFamily: "var(--wus-mono)",
                                }}
                              >
                                {aiExpNhay.toFixed(2)} nháy
                              </strong>
                              <span
                                className={`status-badge ${
                                  aiRoiVal >= 0 ? "active" : "expired"
                                }`}
                              >
                                ROI: {aiRoiVal >= 0 ? "+" : ""}
                                {aiRoiVal.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {aiRank === 1 ? (
                              <span className="status-badge active">
                                ★ 1. Độc Thủ Top 1 (15đ)
                              </span>
                            ) : aiRank === 2 ? (
                              <span className="status-badge active">
                                ★ 2. Song Thủ #2 (15đ)
                              </span>
                            ) : aiRank === 3 ? (
                              <span className="bank-count-badge">
                                3–4. Tam Thủ / Lót #3 (5đ–10đ)
                              </span>
                            ) : aiRank <= 5 ? (
                              <span className="bank-type-badge">
                                5. Ngũ Thủ Top 5 (10đ)
                              </span>
                            ) : aiRank <= 7 ? (
                              <span className="bank-type-badge">
                                6. Thất Thủ Top 7 (10đ)
                              </span>
                            ) : aiRank <= 10 ? (
                              <span className="bank-count-badge">
                                7. Thập Thủ Top 10 (10đ)
                              </span>
                            ) : aiRank <= 15 ? (
                              <span className="bank-count-badge">
                                8. Đều Tay Top 15 (10đ)
                              </span>
                            ) : (
                              <small style={{ color: "var(--wus-text-muted)" }}>
                                Ngoài Top 15
                              </small>
                            )}
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <strong style={{ fontFamily: "var(--wus-mono)" }}>
                              {item.probPct.toFixed(1)}%
                            </strong>{" "}
                            <small style={{ color: "var(--wus-text-muted)" }}>
                              ({item.lift >= 1 ? "+" : ""}
                              {((item.lift - 1) * 100).toFixed(0)}%)
                            </small>
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {item.currentGan === 0 ? (
                              <span className="status-badge active">
                                Vừa về hôm qua
                              </span>
                            ) : item.currentGan >= 10 ? (
                              <span className="status-badge expired">
                                Lô Gan {item.currentGan}N
                              </span>
                            ) : (
                              <span>
                                Nghỉ <strong>{item.currentGan}N</strong>{" "}
                                <small style={{ color: "var(--wus-text-muted)" }}>
                                  (TB: {item.meanCycle.toFixed(1)}N)
                                </small>
                              </span>
                            )}
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <strong>{item.freq7}</strong> lần/7N •{" "}
                            <strong>{item.freq30}</strong> lần/30N ({item.nhay30}*)
                          </td>
                          <td>{renderRhythmDots(item.timeline)}</td>
                          <td style={{ minWidth: "200px" }}>
                            <div className="d-flex flex-wrap gap-1">
                              {item.bestBridge && (
                                <span className="bank-type-badge">
                                  Cầu {item.bestBridge.streak}N (
                                  {item.bestBridge.label})
                                </span>
                              )}
                              {(item.tags || []).map((t, idx) => (
                                <span key={idx} className="bank-count-badge">
                                  {t}
                                </span>
                              ))}
                            </div>
                            {topReason && (
                              <div
                                style={{
                                  fontSize: "11.5px",
                                  color: "var(--wus-text-muted)",
                                  marginTop: "2px",
                                }}
                              >
                                {topReason}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </MasterLayout>
  );
}

export default XsmbPage;
